import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Timer,
  Color,
  DirectionalLight,
  DoubleSide,
  GridHelper,
  Group,
  HemisphereLight,
  LineSegments,
  Material,
  Matrix3,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  OrthographicCamera,
  Plane,
  PMREMGenerator,
  Scene,
  ShaderMaterial,
  Sprite,
  SpriteMaterial,
  SRGBColorSpace,
  Texture,
  Vector2,
  Vector3,
  WebGLRenderer
} from 'three';
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree, MeshBVH } from 'three-mesh-bvh';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js';
import { EdgeSplitModifier } from 'three/examples/jsm/modifiers/EdgeSplitModifier.js';
import { CSS2DObject, CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { GeometryWorkerService, ProcessedGeometry } from '../services/geometry/geometry-worker.service';
// @ts-ignore
import { TrackballControls } from './trackballcontrols';

BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
Mesh.prototype.raycast = acceleratedRaycast;
Object3D.DEFAULT_UP = new Vector3(0, 0, 1);

import { Controls, OutlineWireEffect } from './addons/controls';
import { OrbitPivotManager } from './managers/orbit-pivot.manager';
import { CADEditorVisibilityManager } from './managers/cad-editor-visibility.manager';
import { KeyboardShortcutsManager } from './managers/keyboard-shortcuts.manager';
import { HelperManager } from './managers/helper.manager';
import { DrillBitsManager, MillingDepthPlaneResolver } from './managers/drill-bits.manager';
import { LoadCaseVisualizationManager } from './managers/load-case-visualization.manager';
import { JawsManager, JawsResultProgress } from './managers/jaws.manager';
import { FPSMonitor } from './managers/fps-monitor';
import { GridManager } from './managers/grid.manager';
import { SharpEdgesManager } from './managers/sharp-edges.manager';
import { DesignOverlapManager } from './managers/design-overlap.manager';
import { ThinRegionsManager } from './managers/thin-regions.manager';
import { AssemblyManager } from './managers/assembly.manager';
import { PrintOrientationManager } from './managers/print-orientation.manager';
import { SpaceMouseManager } from './managers/space-mouse.manager';
import { loadSimulatedNavlib, spaceMouseSimRequested } from './addons/space-mouse/navlib';

import * as TWEEN from '@tweenjs/tween.js';
import { DragControls } from 'three/examples/jsm/controls/DragControls.js';
import { ArrowHelper } from './addons/load-helpers/arrowhelper';
import { deriveAxesFromDefinition, deriveFramePoints, LocalFrameAxes } from '../utils/local-frame.util';
import { placeObject, ucsDisplayMatrix } from '../utils/ucs-display.util';
import { lookAtInParentSpace } from './addons/view-frame';
import { coordinateSystemColor } from '../utils/coordinate-systems.util';
import { getAnnotationScale } from './annotation-scale';
import { estimateDesignRegion, TriangleSoup } from './design-region-bounds';
import {
  OVERLAY_COLORS,
  OVERLAY_LABEL_RENDER_ORDER,
  OVERLAY_LINE_WIDTH_CSS_PX,
  OVERLAY_OPACITY,
  OVERLAY_RENDER_ORDER,
  OVERLAY_SIZE,
  OVERLAY_THIN_LINE_WIDTH_CSS_PX,
  OverlayState,
  arrowHead,
  constraintCrossHalfLength,
  createOverlayCone,
  createOverlayLine,
  createOverlayLineMaterial,
  createOverlayMeshMaterial,
  createOverlaySphere,
  createOverlaySphereGeometry,
  overlayUnit,
  setOverlayState,
  triadArmLength
} from './addons/visualization/overlay-style';

import { HttpEventType, HttpRequest } from '@angular/common/http';
import { Easing, Tween } from '@tweenjs/tween.js';
import { BehaviorSubject, Subscription } from 'rxjs';
import type { NgZone } from '@angular/core';
import {
  LoadType,
  ManufacturingMethod,
  PartSubType,
  PartType,
  ProjectType,
  RunType
} from '../models/enums';
import { LoadsGroupItem } from '../models/loadcase.model';
import { SessionService } from '../services/session.service';
import { ThemeService } from '../services/theme/theme.service';
import { UnitConversionService } from '@infinitform/shared-ui';
import { Clipper } from './addons/clipping/clipping';
import { PerFaceMaterial, mergeGeometriesWithFaceIndex } from './addons/per-face-material';
import {
  LIGHTING_PRESETS,
  PART_METALNESS,
  PART_ROUGHNESS,
  PART_REFLECTIVITY,
  PART_CLEARCOAT,
  PART_ENVMAP_INTENSITY,
  CAMERA_FRUSTUM_RATIO_WIDE,
  CAMERA_FRUSTUM_RATIO_MEDIUM,
  CAMERA_FRUSTUM_RATIO_NARROW,
  POLYGON_OFFSET_FACTOR,
  FACE_POLYGON_OFFSET,
  WIRE_LINE_WIDTH_CSS_PX,
  WIRE_SELECTED_LINE_WIDTH_CSS_PX,
  OVERLAY_DEPTH_BIAS_FRACTION,
  BVH_TARGET_LEAF_SIZE,
  MAX_PIXEL_RATIO,
  WIRE_CUTOFF_ANGLE,
  GENERATED_SHAPE_CUTOFF_ANGLE,
  MAIN_THREAD_MESH_PROCESSING_MAX_TRIANGLES,
  LOAD_INDICATOR_DEFAULT_LENGTH,
  LOAD_SPHERE_RADIUS,
  TEXTURE_ANIMATION_INTERVAL_MS,
  RENDER_ACTIVE_WINDOW_MS,
  IDLE_RENDER_INTERVAL_MS,
  DEFAULT_EMPTY_SIZE,
  TRIAD_ORIGIN_MARKER_NAME,
  FRAME_AXIS_COLORS,
  GLOBAL_AXIS_LABELS,
  LOCAL_AXIS_LABELS,
  ANNOTATION_PREVIEW_TILT,
  UCS_PREVIEW_TRIAD_KEY,
  REMOTE_POINT_DRAG_HOLD,
  REFERENCE_POINT_DRAG_HOLD
} from './engine.constants';
import { readablePartColor } from './part-display-color';
import {
  PickedPointMarkerSpec,
  PickedPointMarkers,
  localPointMarkerRadius
} from './addons/visualization/picked-point-markers';
import { viewportBackground } from '../utils/viewport-background.util';
import {
  ProtoAssembly,
  ProtoFace,
  ProtoPart,
  ProtoWire,
  EngineObject,
  FEAResultMeshSet,
  FEAResultInfoV2,
  DimensionsHelper,
  TransformControlEvent
} from './engine.types';
import { Selector } from './addons/selector';
import { FEA_VERTEX_SHADER, FEA_FRAGMENT_SHADER } from './shaders/fea-displacement.shaders';
import { ViewCubeHelper } from './addons/view-cube-helper';
import { calculateCenter, getLoadIndicatorScale } from './helpers';
import { InfiniteGridHelper } from './addons/infinite-grid';
import { DEFAULT_ENGINE_GRAPHICS, EngineGraphicsOptions, LightingStyle } from './graphics-options';
import type { MorphModels, RenderPart, RenderStudioHost } from './addons/render-studio/render-studio';
import { renderSupersampled } from './addons/viewport-capture';

/** Sub-pixel passes averaged into each viewport screenshot. */
const SCREENSHOT_SAMPLES = 16;

/** How a frame marker was drawn, kept so it can be drawn again at a new size. */
type TriadSpec =
  | { kind: 'triad'; origin: Vector3; axes: LocalFrameAxes; color: string }
  | { kind: 'cross'; origin: Vector3; axes: LocalFrameAxes; locked: boolean[]; color: string | null };

/**
 * Clip-space depth offset that pushes input part faces behind a coincident
 * optimized/result overlay: OVERLAY_DEPTH_BIAS_FRACTION of the model's size,
 * converted to NDC for an orthographic camera whose depth range is `depthRange`.
 */
export function overlayDepthBiasNdc(modelMaxDimension: number, depthRange: number): number {
  if (!(modelMaxDimension > 0) || !(depthRange > 0)) return 0;
  return (2 * OVERLAY_DEPTH_BIAS_FRACTION * modelMaxDimension) / depthRange;
}

class Engine {
  /**
   * Shared IBL environment texture: a PMREM-convolved RoomEnvironment, built
   * once per renderer in init() (see Controls.createEnvironment()) and also
   * assigned to `scene.environment`. Exposed for managers that build their own
   * materials (drill bits, optimized meshes) — see EngineContext.envMap.
   */
  public envMap: Texture | null = null;
  private pmremGenerator?: PMREMGenerator;

  // Graphics options (lighting style): see graphics-options.ts. Starts at the
  // documented default so the engine renders sensibly before any settings
  // arrive; applyGraphicsOptions() is idempotent and cheap when the requested
  // values already match.
  private graphicsOpts: EngineGraphicsOptions = { ...DEFAULT_ENGINE_GRAPHICS };
  private lightingRig?: { key: DirectionalLight; fill: DirectionalLight; hemi: HemisphereLight };

  public camera!: OrthographicCamera;
  public scene!: Scene;
  public controls!: TrackballControls;
  public renderer!: WebGLRenderer;
  public renderer_css2d!: CSS2DRenderer;

  public outlineEffect!: OutlineWireEffect;
  private canvasResizeObserver?: ResizeObserver;

  public gridHelper!: GridHelper;
  public infiniteGridHelper: InfiniteGridHelper | null = null;
  public useInfiniteGrid = true;
  public viewHelper!: ViewCubeHelper;
  public selector!: Selector;
  public clock: Timer = new Timer();
  private lastTextureAnimTime = 0;
  private readonly textureAnimInterval = TEXTURE_ANIMATION_INTERVAL_MS;

  public objects: EngineObject[] = [];
  public get objectsDesign() {
    return this.objects.filter((o) => o.type === PartType.DESIGN);
  }
  public get objectsNondesign() {
    return this.objects.filter((o) => o.type === PartType.NONDESIGN);
  }

  public group: Group = new Group();
  public groups: Group[] = [];

  public optimizedGroup: Group = new Group();
  public optimizedGroupParametric: Group = new Group();
  public infoGroup: Group = new Group();
  public helperGroup: Group = new Group();

  public symmetryPlanes: { xy?: Group; xz?: Group; yz?: Group } = {};
  public tweens: TWEEN.Tween<any>[] = [];
  // DRILL BITS
  public drillbitMesh: any;
  public drillBits: { x1?: Mesh; y1?: Mesh; z1?: Mesh; x2?: Mesh; y2?: Mesh; z2?: Mesh } = {
    x1: undefined,
    y1: undefined,
    z1: undefined,
    x2: undefined,
    y2: undefined,
    z2: undefined
  };

  public arrowHelper?: Group;
  public arrowHelperTween?: Tween<any>;
  // Z Rotation Arrows for SoftJaws
  public zRotationArrowsGroup: Group = new Group();

  // Orbit pivot manager (extracted)
  public orbitPivot!: OrbitPivotManager;
  public cadEditorVisibility!: CADEditorVisibilityManager;
  public keyboardShortcuts!: KeyboardShortcutsManager;
  public spaceMouse?: SpaceMouseManager;
  public helpers!: HelperManager;
  public drillBitsManager!: DrillBitsManager;
  public loadCaseVisualization!: LoadCaseVisualizationManager;
  public jawsManager!: JawsManager;
  public gridManager!: GridManager;
  public sharpEdges!: SharpEdgesManager;
  public designOverlap!: DesignOverlapManager;
  public thinRegions!: ThinRegionsManager;
  public printOrientation!: PrintOrientationManager;
  public assemblyManager!: AssemblyManager;

  // Selection for focus (F key) - components can set this
  public selectedObjects: Object3D[] = [];

  public keyboardControlPressed = false;

  public helpersVisible = true;

  // Comparison mode flag - when true, windowResize is handled by comparison view
  public comparisonModeActive = false;

  // PROTO
  public assembly!: ProtoAssembly;
  public assemblyOptimized!: Group;
  public assemblyOptimizedParametric!: Group;
  public assemblyOptimizedInfo: FEAResultMeshSet[] = [];
  public currentLoadCase: string | null = null;
  private _highlightLoadCase = false;

  public get highlightLoadCase(): boolean {
    return this._highlightLoadCase;
  }

  /** Domain markers only exist while a load case is highlighted. */
  public set highlightLoadCase(value: boolean) {
    this._highlightLoadCase = value;
    if (!value) this.clearDomainFrameTriads();
  }

  public currentFEAOutput: 'organic' | 'prismatic' = 'organic';

  public progressUpdate = new BehaviorSubject({ total: 0, current: 0 });

  // Dimensions Helper
  public dimensionsHelper!: DimensionsHelper[];
  public dimensionsHelperVisible = false;
  public toggleDimensionsHelper(check?: boolean) {
    if (check) {
      this.dimensionsHelper[0].toggle(this.dimensionsHelperVisible);
      this.dimensionsHelper[1].toggle(this.dimensionsHelperVisible);
      this.dimensionsHelper[2].toggle(this.dimensionsHelperVisible);
      return;
    }
    this.dimensionsHelperVisible = !this.dimensionsHelperVisible;
    this.dimensionsHelper[0].toggle(this.dimensionsHelperVisible);
    this.dimensionsHelper[1].toggle(this.dimensionsHelperVisible);
    this.dimensionsHelper[2].toggle(this.dimensionsHelperVisible);
  }

  public refreshDimensionsHelper(): void {
    this.pivot.updateWorldMatrix(true, true);
    this.group.updateWorldMatrix(true, true);
    const { box } = this.calculateGroupMinMaxCenter(this.group, this.viewFrame.matrix.clone().invert());
    this.dimensionsHelper = Controls.createDimensionsHelper(this, box, this.viewFrame.matrix);
    this.toggleDimensionsHelper(true);
  }

  // Pivot
  public pivot = new Group();

  /** Set once init() has placed the pivot. */
  private ucsViewReady = false;

  /**
   * Content in the UCS frame, the frame the run turns the model into: symmetry
   * planes, drill bits, milling depth planes, the draw-direction arrow,
   * reaction forces and the max-stress marker. The model is always shown as
   * imported, so this holder carries the UCS turn undone.
   */
  public readonly ucsFrame = new Group();
  /** Each part's load and remote-mass markers, in the model's own coordinates like the loads. */
  public readonly partHelperGroup = new Group();

  /**
   * Holds the camera and the ground grid, in view space. It carries the view
   * turn undone, so the screen shows the model turned while the scene, picks
   * and every stored input stay in world coordinates. Identity unless the
   * user coordinate system turns the view; see `viewTurn`.
   */
  public readonly viewFrame = new Group();

  // Rotation controls for soft jaws
  public rotationControls?: TransformControls;

  // Normal Arrow Helper
  public normalArrowHelper = new ArrowHelper('line', new Vector3(0, 0, 0), new Vector3(0, 0, 40), 0, 0x0000ff);
  public isOptimizationView = false;
  public reactionForces?: boolean = false;
  public animateTexture: boolean = false;
  public deformScale: number = 1;
  public showMaxStressMarker = false;

  // Clipping
  public clipper: Clipper[] = [];
  public clippingEnabled = false;

  public animationFired = false;
  private resizePending = false;

  constructor(
    public canvas: HTMLCanvasElement,
    public viewHelperContainer: HTMLDivElement,
    public css2dContainer: HTMLDivElement,
    public session: SessionService,
    public themeService: ThemeService,
    public unitConversionService: UnitConversionService,
    public readonly zone?: NgZone
  ) {
    // AssemblyManager must be available before init() since assembly
    // methods (setAssembly, setOptimizedAssembly, etc.) are called
    // by EngineInitializerService before init() runs.
    this.assemblyManager = new AssemblyManager(this);
    this.ucsFrame.name = 'ucs-frame';
  }

  async init(projectStatus: number) {
    this.scene = Controls.createScene();
    this.renderer = Controls.createEngine(this.scene, this.canvas);
    this.renderer_css2d = Controls.createCSS2DRenderer(this.css2dContainer, this.canvasWidth, this.canvasHeight);
    this.camera = Controls.createCamera(this.scene);
    this.viewFrame.name = 'view-frame';
    this.scene.add(this.viewFrame);
    this.viewFrame.add(this.camera);
    lookAtInParentSpace(this.camera);
    this.controls = Controls.createTrackballControls(this.camera, this.canvas, this.scene);

    // TrackballControls caches the canvas rect and maps pointer coordinates
    // through it, so it has to be told when that rect moves. It used to be
    // told every frame, which meant a getBoundingClientRect() -- a forced
    // synchronous layout -- on every single frame. Observing the element
    // instead catches the cases a window resize misses (sidenav collapse,
    // panel toggles) without measuring when nothing moved.
    this.canvasResizeObserver = new ResizeObserver(() => {
      (this.controls as TrackballControls).handleResize?.();
      this.requestRender();
    });
    this.canvasResizeObserver.observe(this.canvas);
    this.orbitPivot = new OrbitPivotManager(this);
    this.cadEditorVisibility = new CADEditorVisibilityManager(this);
    this.helpers = new HelperManager(this);
    this.drillBitsManager = new DrillBitsManager(this);
    this.loadCaseVisualization = new LoadCaseVisualizationManager(this);
    this.jawsManager = new JawsManager(this);
    this.gridManager = new GridManager(this);
    this.sharpEdges = new SharpEdgesManager(this);
    this.designOverlap = new DesignOverlapManager(this);
    this.thinRegions = new ThinRegionsManager(this);
    this.printOrientation = new PrintOrientationManager(this);
    this.controls.addEventListener('start', this.orbitPivot.controlsActionStartHandler);
    this.controls.addEventListener('end', this.orbitPivot.controlsActionEndHandler);
    this.controls.addEventListener('change', this.requestRender);

    await this.initMesh();

    if (this.session.project.projectType === ProjectType.TopologyOptimization) {
      if (projectStatus === 4) {
        if (this.session.organicAssemblyAvailable) {
          this.initOptimizedMesh();
        }
        if (this.session.parametricAssemblyAvailable) {
          this.initOptimizedMeshParametric();
        }
      }
    }

    // Anything read while the parts were still arriving measured only some of them.
    this.invalidateCachedObjectInfo();
    const { center, size, box } = this.objectInfo;
    this.modelMaxDimension = Math.max(size.x, size.y, size.z);
    // Part load arrows are made before the model's size is known; every path
    // that shows one relies on it already being at the model's scale.
    this.loadCaseVisualization.refreshIndicatorSizes();

    // Shared IBL environment (no network fetch — a PMREM-convolved room, not a
    // photo HDR) and the camera-headlamp + hemisphere lighting rig. Both need
    // the camera (added to the scene in createCamera) and the object bounds.
    // The environment is cosmetic (reflections only): if PMREM generation
    // fails on some driver, parts still render lit by the rig below.
    try {
      const { texture, generator } = Controls.createEnvironment(this.renderer);
      this.envMap = texture;
      this.pmremGenerator = generator;
      this.scene.environment = texture;
    } catch (e) {
      console.warn('[Engine] PMREM environment generation failed:', e);
    }
    this.lightingRig = Controls.createLightingRig(this.camera, this.scene, center, this.modelMaxDimension);
    this.applyLightingStyle(this.graphicsOpts.lighting);

    this.scene.add(this.infoGroup);
    this.scene.add(this.helperGroup);
    this.scene.add(this.ucsFrame);
    this.scene.add(this.partHelperGroup);
    this.controls.target.copy(center);

    Controls.fitCameraToCenteredObject(this.camera, size, center);
    this.orbitPivot.init(size, center);

    this.selector = Controls.createRaycaster(this.camera, this.canvas, this.groups, this.group, this.objects, this);
    this.viewHelper = Controls.createCameraCubeControls(this, this.viewHelperContainer);
    this.outlineEffect = Controls.createOutlineEffectWithLineMaterial(this.session);
    this.gridHelper = Controls.createGridHelper(this.gridHelper, size, this.viewFrame, this.canvas, center);
    if (this.useInfiniteGrid && this.gridHelper) {
      this.gridHelper.visible = false;
      this.gridHelper.userData['xLine'].visible = false;
      this.gridHelper.userData['yLine'].visible = false;
      this.gridHelper.userData['groundPlane'].visible = false;
    }
    this.createInfiniteGrid(size, center);

    this.drillBitsManager.generateSymmetryPlanes(Math.max(size.x, size.y, size.z), center, size);

    this.dimensionsHelper = Controls.createDimensionsHelper(this, box);

    if (this.session.project.projectType === ProjectType.TopologyOptimization) {
      await this.drillBitsManager.initDrillBits();
    }

    this.scene.add(this.pivot);
    this.pivot.position.set(this.objectInfo.center.x, this.objectInfo.center.y, this.objectInfo.center.z);

    this.pivot.attach(this.group);

    if (this.session.project.projectType === ProjectType.SoftJaws) {
      this.scene.add(this.jaws.jawsPivot);
      this.jaws.jawsPivot.position.set(this.objectInfo.center.x, this.objectInfo.center.y, this.objectInfo.center.z);
      // Result: X,Y from model center (pivot offset), Z at 0 (binary output has correct absolute Z)
      this.jaws.result.position.set(this.objectInfo.center.x, this.objectInfo.center.y, 0);
      this.scene.add(this.jaws.result);
    }

    this.normalArrowHelper.visible = false;
    this.scene.add(this.normalArrowHelper);

    this.session.project.userCoordinateSystem.origin = [
      parseFloat(this.objectInfo.center.x.toFixed(4)),
      parseFloat(this.objectInfo.center.y.toFixed(4)),
      parseFloat(this.objectInfo.center.z.toFixed(4))
    ];

    this.ucsViewReady = true;
    if (this.session.project.userCoordinateSystem.enabled) {
      setTimeout(() => {
        this.ucsUpdate();
      });
    }

    this.themeSubscription = this.themeService.appliedTheme$.subscribe((theme) => {
      setTimeout(() => {
        this.applyThemeToGrid();
        this.viewHelper?.setTheme(theme === 'dark');
        // Was only ever set once, at init: switching theme after load left the
        // viewport on its startup colour.
        this.scene.background = new Color(viewportBackground());
        this.requestRender();
      });
    });

    this.startAnimationLoop();
    if (this.zone) {
      // Work that runs inside Angular wakes the loop through onStable.
      this.zoneStableSubscription = this.zone.onStable.subscribe(this.requestRender);
    }
    this.requestRender();

    // The optimized and result meshes are often hidden at first; compile
    // their programs in the background after the first frame so showing them
    // later does not stall on a shader compile.
    setTimeout(() => {
      for (const group of [this.optimizedGroup, this.optimizedGroupParametric, this.infoGroup]) {
        if (group.children.length > 0) void this.precompile(group);
      }
    });

    // Initialize keyboard shortcuts (F = focus, H = home)
    this.keyboardShortcuts = new KeyboardShortcutsManager(this, this.orbitPivot);
    this.keyboardShortcuts.init();

    // 3Dconnexion 3D mouse. The driver talks over a websocket at up to 60 Hz,
    // so it is connected outside Angular; only its button commands come back in.
    const zone = this.zone;
    const simulated = spaceMouseSimRequested();
    this.spaceMouse = new SpaceMouseManager(this, this.orbitPivot, this.keyboardShortcuts, {
      runCommand: zone ? (command): void => zone.run(command) : undefined,
      onConnectionChange: (connected): void =>
        this.session.spaceMouseStatus.set(connected ? (simulated ? 'simulated' : 'connected') : 'disconnected'),
      ...(simulated ? { load: loadSimulatedNavlib, platformSupported: (): boolean => true } : {})
    });
    const spaceMouse = this.spaceMouse;
    const startSpaceMouse = (): void => void spaceMouse.start();
    if (zone) zone.runOutsideAngular(startSpaceMouse);
    else startSpaceMouse();

    // Mouse Bindings
    setTimeout(() => {
      this.session.initMousePreset();
      this.session.initGraphicsPreset();
      this.windowResize();
    });

    // A flat theme background.
    this.scene.background = new Color(viewportBackground());

    this.addContextLossHandlers();
  }

  /**
   * Applies the settings side's graphics options (currently: lighting style).
   * Idempotent — a call with values that already match is a no-op — and cheap
   * enough to call on every settings change. Safe to call before init() (the
   * values are simply stored and picked up there).
   */
  public applyGraphicsOptions(options: Partial<EngineGraphicsOptions>): void {
    const next: EngineGraphicsOptions = { ...this.graphicsOpts, ...options };
    const lightingChanged = next.lighting !== this.graphicsOpts.lighting;
    if (!lightingChanged) return;

    this.graphicsOpts = next;
    if (!this.scene || !this.camera) return; // init() applies graphicsOpts once the rig exists

    this.applyLightingStyle(next.lighting);
    this.requestRender();
  }

  public get graphicsOptions(): EngineGraphicsOptions {
    return { ...this.graphicsOpts };
  }

  /** Sets key/fill/hemisphere intensities and the shared environment's contribution for `style`. */
  private applyLightingStyle(style: LightingStyle): void {
    const preset = LIGHTING_PRESETS[style];
    if (!preset || !this.lightingRig || !this.scene) return;
    this.lightingRig.key.intensity = preset.keyIntensity;
    this.lightingRig.fill.intensity = preset.fillIntensity;
    this.lightingRig.hemi.intensity = preset.hemiIntensity;
    this.scene.environmentIntensity = preset.environmentIntensity;
  }

  // ── WebGL context loss ─────────────────────────────────────────────
  // A GPU reset (Windows TDR, driver update, an Optimus GPU switch) or the
  // browser reclaiming contexts loses the context. three re-uploads geometry,
  // textures and programs on restore by itself; the engine has to keep the
  // browser willing to restore, hide the HTML overlay while there is no 3D
  // behind it, rebuild the post-processing targets and draw again.
  public contextLost = false;

  private readonly onContextLost = (event: Event): void => {
    event.preventDefault();
    this.contextLost = true;
    if (this.renderer_css2d?.domElement) this.renderer_css2d.domElement.style.visibility = 'hidden';
  };

  private readonly onContextRestored = (): void => {
    this.contextLost = false;
    if (this.renderer_css2d?.domElement) this.renderer_css2d.domElement.style.visibility = '';
    // The PMREM environment is a render target texture with no source image,
    // so three cannot re-upload it on its own like it does for image-backed
    // textures: regenerate it against the new context, then repoint every
    // material that captured the old (now-invalid) texture object at construction.
    try {
      this.pmremGenerator?.dispose();
      const oldEnvMap = this.envMap;
      const { texture, generator } = Controls.createEnvironment(this.renderer);
      this.envMap = texture;
      this.pmremGenerator = generator;
      this.scene.environment = texture;
      if (oldEnvMap) {
        this.scene.traverse((obj) => {
          const materials = (obj as Mesh).material;
          for (const m of Array.isArray(materials) ? materials : materials ? [materials] : []) {
            const physical = m as unknown as { envMap?: Texture | null; needsUpdate?: boolean };
            if (physical.envMap === oldEnvMap) {
              physical.envMap = texture;
              physical.needsUpdate = true;
            }
          }
        });
        oldEnvMap.dispose();
      }
    } catch (e) {
      console.warn('[Engine] PMREM environment regeneration failed after context restore:', e);
    }
    this.windowResize();
    this.requestRender();
  };

  private addContextLossHandlers(): void {
    this.canvas.addEventListener('webglcontextlost', this.onContextLost);
    this.canvas.addEventListener('webglcontextrestored', this.onContextRestored);
  }

  /**
   * Compiles the shader programs `object` needs (default: the whole scene) so
   * its first appearance does not block on compilation. Uses parallel shader
   * compilation where the driver offers it. Never rejects.
   */
  public async precompile(object: Object3D = this.scene): Promise<void> {
    if (!this.renderer || !this.camera || this.contextLost) return;
    try {
      await this.renderer.compileAsync(object, this.camera, this.scene);
    } catch (e) {
      console.warn('[Engine] shader precompile failed:', e);
    }
  }

  // ── Backward-compatible delegates to OrbitPivotManager ──────────────
  public showTemporaryPivot(): void { this.orbitPivot.showTemporary(); }
  public hideTemporaryPivot(): void { this.orbitPivot.hideTemporary(); }
  public clearOrbitPivot(): void { this.orbitPivot.clear(); }
  public resetOrbitPivot(): void { this.orbitPivot.reset(); }

  // ── Backward-compatible delegates to KeyboardShortcutsManager ──────

  public get gridVisible(): boolean { return this.gridManager.visible; }
  public set gridVisible(value: boolean) { this.gridManager.visible = value; }
  public toggleGrid(): void { this.gridManager.toggle(); }
  private createInfiniteGrid(size: Vector3, center: Vector3): void { this.gridManager.createInfiniteGrid(size, center); }
  private disposeInfiniteGrid(): void { this.gridManager.disposeInfiniteGrid(); }

  public focusSelection(): void { this.keyboardShortcuts.focusSelection(); }
  public fitAll(): void { this.keyboardShortcuts.fitAll(); }
  public resetHomeView(): void { this.keyboardShortcuts.resetHomeView(); }
  public setSelectedObjects(objects: Object3D[]): void { this.selectedObjects = objects; }

  public clippingPlanes = [new Plane(), new Plane(), new Plane()];
  public initClipping(): void {
    const prevClipperPositions = this.clipper[0]
      ? {
          x: this.clipper[0].planePositions.x,
          y: this.clipper[0].planePositions.y,
          z: this.clipper[0].planePositions.z,
        }
      : null;

    const isStaleClipperObject = (o: Object3D): boolean =>
      o.name === 'PlaneMeshX' || o.name === 'PlaneMeshY' || o.name === 'PlaneMeshZ' ||
      o.name.endsWith('_SurfaceModel') || o.name.endsWith('_BackSideModel') ||
      o.name.endsWith('_StencilFront') || o.name.endsWith('_StencilBack') ||
      o.name.endsWith('_Collider');

    // `_Collider` excluded — must stay hidden (basic white material that would paint over the model).
    const isClippingVisibilityTarget = (o: Object3D): boolean =>
      o.name === 'PlaneMeshX' || o.name === 'PlaneMeshY' || o.name === 'PlaneMeshZ' ||
      o.name.endsWith('_SurfaceModel') || o.name.endsWith('_BackSideModel') ||
      o.name.endsWith('_StencilFront') || o.name.endsWith('_StencilBack');

    const staleObjects: Object3D[] = [];
    this.scene.traverse((o) => {
      if (isStaleClipperObject(o)) staleObjects.push(o);
    });
    const disposeMeshResources = (o: Object3D) => {
      (o as { geometry?: { dispose?: () => void } }).geometry?.dispose?.();
      const mat = (o as { material?: unknown }).material;
      if (mat && typeof (mat as { dispose?: () => void }).dispose === 'function') {
        (mat as { dispose: () => void }).dispose();
      }
    };
    for (const obj of staleObjects) {
      obj.removeFromParent();
      disposeMeshResources(obj);
    }
    if (this.clipper.length > 0) {
      for (const c of this.clipper) {
        for (const line of [c.outlineLinesX, c.outlineLinesY, c.outlineLinesZ]) {
          if (!line) continue;
          line.removeFromParent();
          line.geometry?.dispose?.();
          const lineMat = (line as { material?: unknown }).material;
          if (lineMat && typeof (lineMat as { dispose?: () => void }).dispose === 'function') {
            (lineMat as { dispose: () => void }).dispose();
          }
        }
      }
      this.clipper = [];
    }

    const clipper = new Clipper(this.scene, this.clippingPlanes);
    let usedSetObject = false;

    // Phase 1: Process optimized meshes and wire lines
    this.scene.traverse((o) => {
      if (o instanceof Mesh && (o.name === 'OptimizedOrganicMesh' || o.name === 'OptimizedPrismaticMesh')) {
        if (!usedSetObject) {
          clipper.setObject(o);
          usedSetObject = true;
        } else {
          o.material.clippingPlanes = this.clippingPlanes;
          clipper.addObject(o);
        }
      } else if (
        o instanceof Line2 &&
        o.userData &&
        (o.userData['line_type'] === 'OptimizedOrganicMeshLine' ||
          o.userData['line_type'] === 'OptimizedPrismaticMeshLine' ||
          o.userData['line_type'] === 'AssemblyMeshLine')
      ) {
        o.material.clippingPlanes = this.clippingPlanes;
      }
    });

    // Assembly + FEA meshes are non-manifold — skip stencil cap, keep edge detection.
    for (const obj of this.objects) {
      let mergedMesh: Mesh | undefined;
      obj.group.traverse((child: Object3D) => {
        if (child instanceof Mesh && child.userData?.['isMergedMesh']) {
          mergedMesh = child;
        }
      });
      if (mergedMesh) {
        clipper.addObject(mergedMesh, false);
      }
    }

    this.infoGroup.children.forEach((child) => {
      if (child instanceof Mesh && child.material) {
        (child.material as MeshBasicMaterial).clippingPlanes = this.clippingPlanes;
        clipper.addChildObject(child, false);
      }
    });

    this.clipper = [clipper];

    // Recompute all BVH asynchronously
    clipper.computeAllBVHAsync(this.geometryWorker).catch(() => {});

    // Set plane positions based on overall object bounds
    const { box } = this.objectInfo;
    clipper.setMinMax(
      box.min.x - 10,
      box.max.x + 10,
      box.min.y - 10,
      box.max.y + 10,
      box.min.z - 10,
      box.max.z + 10
    );
    // Slider values are offsets from the model mid-plane (0 = centre,
    // ± = either side). Default is 0 from setMinMax; only override when
    // restoring a previous slider position across re-init.
    if (prevClipperPositions) {
      clipper.setPosition(prevClipperPositions.x, 'x');
      clipper.setPosition(prevClipperPositions.y, 'y');
      clipper.setPosition(prevClipperPositions.z, 'z');
    }

    this.renderer.localClippingEnabled = this.clippingEnabled;
    this.scene.traverse((o) => {
      if (isClippingVisibilityTarget(o)) {
        o.visible = this.clippingEnabled;
      }
    });
    if (clipper.outlineLinesX) clipper.outlineLinesX.visible = this.clippingEnabled;
    if (clipper.outlineLinesY) clipper.outlineLinesY.visible = this.clippingEnabled;
    if (clipper.outlineLinesZ) clipper.outlineLinesZ.visible = this.clippingEnabled;
  }

  /** Applies the UCS to the view: the model stays as imported and the UCS-frame content turns. */
  public ucsUpdate() {
    this.applyUcsView();
    if (
      this.isOptimizationView &&
      (this.session.project.manufacturingMethod === ManufacturingMethod.InjectionMoldingWithDrawDirection ||
        this.session.project.manufacturingMethod === ManufacturingMethod.DieCasting)
    ) {
      this.session.engine?.drawDirectionVectorChange(undefined);
    } else {
      this.session.engine?.disposeArrow();
    }
  }

  /**
   * Places the UCS-frame holder, the result groups and the view for the UCS
   * and rebuilds the view helpers. The scene keeps the model as imported: a
   * turn the view shows is the camera's, see `viewTurn`.
   */
  private applyUcsView(): void {
    const ucs = this.session.project.userCoordinateSystem;
    this._cachedDesignUcsFrameInfo = null;
    this._cachedDesignRegionUcsFrameInfo = null;
    this._cachedUcsFrameInfo = null;
    if (ucs.enabled) this.invalidateCachedObjectInfo();
    placeObject(this.ucsFrame, this.ucsTurn().invert());
    this.alignResultsToUcs();
    this.applyViewTurn();
    this.drillBitsManager?.fitSymmetryPlanes();
    this.drillBitsManager?.fitMillingDepthPlanes();
    this.rebuildViewHelpers();
    this.updateCoordinateSystemTriads();
  }

  /**
   * Whether the view shows the UCS turn: only for a turn set in the UCS tab.
   * A turn from aligning to a coordinate system stays hidden, and the print
   * view shows the build plate as it is.
   */
  private get viewShowsUcsTurn(): boolean {
    const ucs = this.session.project?.userCoordinateSystem;
    return !!ucs?.enabled && !ucs.alignedSystemId && this.ucsViewReady && !this.printOrientation?.active;
  }

  /** The turn the view shows the model with; identity unless `viewShowsUcsTurn`. */
  public viewTurn(): Matrix4 {
    return this.viewShowsUcsTurn ? this.ucsTurn() : new Matrix4();
  }

  /**
   * Places the view frame for `viewTurn`. The same world point stays in the
   * middle of the screen, so the model turns in place. Returns whether the
   * view changed.
   */
  public applyViewTurn(): boolean {
    if (!this.camera || !this.controls) return false;
    const before = this.viewFrame.matrix.clone();
    placeObject(this.viewFrame, this.viewTurn().invert());
    if (this.viewFrame.matrix.equals(before)) return false;
    this.viewFrame.updateMatrixWorld(true);

    // Old view space to world, then to the new view space.
    const shift = this.viewFrame.matrix.clone().invert().multiply(before);
    const target = this.controls.target.clone().applyMatrix4(shift);
    this.camera.position.add(target.clone().sub(this.controls.target));
    this.controls.target.copy(target);
    this.clearOrbitPivot();
    this.controls.update();
    this.requestRender();
    return true;
  }

  /** Follows a change to whether the view may show the UCS turn (the print view). */
  public refreshViewTurn(): void {
    if (this.applyViewTurn()) this.rebuildViewHelpers();
  }

  /** The model's bounds in view space, where the camera and the grid are. */
  get viewObjectInfo(): { center: Vector3; size: Vector3; box: Box3 } {
    return this.viewShowsUcsTurn ? this.ucsFrameObjectInfo : this.objectInfo;
  }

  /** A world point in view space, where the camera, its target and the grid are. */
  public toViewSpace(worldPoint: Vector3): Vector3 {
    this.viewFrame.updateMatrixWorld();
    return this.viewFrame.worldToLocal(worldPoint.clone());
  }

  /** A view-space point in world coordinates. */
  public toWorldSpace(viewPoint: Vector3): Vector3 {
    this.viewFrame.updateMatrixWorld();
    return this.viewFrame.localToWorld(viewPoint.clone());
  }

  /** A world box in view space: the box around its corners as the view turns them. */
  public toViewBox(box: Box3): Box3 {
    this.viewFrame.updateMatrixWorld();
    return box.clone().applyMatrix4(this.viewFrame.matrixWorld.clone().invert());
  }

  /** Grid, infinite grid and dimensions, rebuilt around the model as it is shown. */
  public rebuildViewHelpers(): void {
    const { size, center, box } = this.viewObjectInfo;
    this.gridHelper = Controls.createGridHelper(this.gridHelper, size, this.viewFrame, this.canvas, center);
    if (this.useInfiniteGrid && this.gridHelper) {
      this.gridHelper.visible = false;
      this.gridHelper.userData['xLine'].visible = false;
      this.gridHelper.userData['yLine'].visible = false;
      this.gridHelper.userData['groundPlane'].visible = false;
    }
    this.createInfiniteGrid(size, center);
    this.applyThemeToGrid();
    this.dimensionsHelper = Controls.createDimensionsHelper(this, box, this.viewFrame.matrix);
    this.toggleDimensionsHelper(true);
  }

  /** A world point in UCS-frame coordinates, where `ucsFrame` content is placed. */
  public toUcsFrame(worldPoint: Vector3): Vector3 {
    this.ucsFrame.updateWorldMatrix(true, false);
    return this.ucsFrame.worldToLocal(worldPoint.clone());
  }

  /** A UCS-frame direction (a reaction force, say) in the model's own axes. */
  public ucsDirectionToModel(direction: Vector3): Vector3 {
    return direction.clone().applyMatrix3(new Matrix3().setFromMatrix4(this.ucsTurn().invert()));
  }

  /** A direction in the model's own axes in UCS-frame coordinates, for content placed in `ucsFrame`. */
  public modelDirectionToUcs(direction: Vector3): Vector3 {
    return direction.clone().applyMatrix3(new Matrix3().setFromMatrix4(this.ucsTurn()));
  }

  /**
   * Whether the results are in the model's own frame rather than the UCS
   * frame: an FEA run solves the model as imported, without the UCS turn.
   */
  public get resultsInModelFrame(): boolean {
    return this.session.project.runType === RunType.FiniteElementAnalysis;
  }

  public coordinateSystemTriads: Group = new Group();
  private frameTriadKeys = new Map<string, Object3D>();

  private disposeTriad(object: Object3D): void {
    object.removeFromParent();
    object.traverse((child) => {
      const disposable = child as Mesh | LineSegments;
      disposable.geometry?.dispose();
      const material = disposable.material as Material | Material[] | undefined;
      const materials = Array.isArray(material) ? material : material ? [material] : [];
      for (const each of materials) {
        // Dropped from the resize registry first, or it keeps a disposed
        // material alive and writes to it on every resize.
        if (each instanceof LineMaterial) this.triadLineMaterials.delete(each);
        each.dispose();
      }
    });
  }

  public clearFrameTriad(key: string): void {
    const existing = this.frameTriadKeys.get(key);
    if (!existing) return;
    this.disposeTriad(existing);
    this.frameTriadKeys.delete(key);
    this.frameTriadSpecs.delete(key);
  }

  /**
   * Clears every domain marker, leaving the UCS tab's preview alone. Load and
   * boundary condition markers are rebuilt from the active load case, so they
   * are cleared whenever that visualisation is rebuilt or torn down.
   */
  public clearDomainFrameTriads(): void {
    // Guards a field-initializer ordering trap: highlightLoadCase is declared
    // well before this registry, so a write to it during construction must
    // not dereference the registry before it exists.
    if (!this.frameTriadKeys) return;
    for (const key of [...this.frameTriadKeys.keys()]) {
      if (key !== UCS_PREVIEW_TRIAD_KEY) this.clearFrameTriad(key);
    }
  }

  /**
   * Frame markers are children of the model group, so their coordinates are
   * the model's own - the ones frames are authored in and the optimizer
   * reads - and they follow the model under the viewport's orientation.
   */
  private attachTriadRoot(): Group {
    if (this.coordinateSystemTriads.parent !== this.group) {
      this.coordinateSystemTriads.removeFromParent();
      this.group.add(this.coordinateSystemTriads);
    }
    return this.coordinateSystemTriads;
  }

  /** Triad materials whose resolution the resize handler keeps current. */
  private triadLineMaterials = new Set<LineMaterial>();

  /** Axis-letter textures, keyed by letter and colour. See triadLabelMaterial. */
  private triadLabelTextures = new Map<string, CanvasTexture>();

  /** Sample markers shown while the annotation size is being dragged. */
  private annotationPreview?: Group;

  /**
   * How each frame marker was drawn. Their geometry is built at a fixed
   * length, so they cannot be rescaled in place the way a load arrow can --
   * they have to be drawn again, and their owners are spread across the
   * selector, the visualization manager and the beam editor, none of which
   * watches the annotation size.
   */
  private frameTriadSpecs = new Map<string, TriadSpec>();

  /**
   * An axis triad at `origin` (model coordinates) oriented by `axes`, plus a
   * dot in the frame's colour. Keyed so several callers (the UCS preview, a
   * selected load) can each own one without clearing the others.
   */
  public drawFrameTriad(key: string, origin: Vector3, axes: LocalFrameAxes, color: string): void {
    this.clearFrameTriad(key);
    const root = this.attachTriadRoot();

    const size = this.objectInfo?.size;
    const holder = new Group();
    this.addTriadAxes(holder, origin, [axes.ex, axes.ey, axes.ez], triadArmLength(size), 'normal', LOCAL_AXIS_LABELS);

    const marker = createOverlaySphere(overlayUnit(size) * OVERLAY_SIZE.pointRadius, color);
    marker.name = TRIAD_ORIGIN_MARKER_NAME;
    marker.position.copy(origin);
    holder.add(marker);

    root.add(holder);
    this.frameTriadKeys.set(key, holder);
    this.frameTriadSpecs.set(key, { kind: 'triad', origin: origin.clone(), axes, color });
  }

  /**
   * Three arrows in the global axis helper's order and colours, so a local
   * frame reads the same way, each with its letter just past the tip. Local
   * frames are lettered X′ Y′ Z′, as in the panels, so they are not taken for
   * the global axes.
   */
  private addTriadAxes(
    holder: Group,
    origin: Vector3,
    axes: readonly (readonly [number, number, number])[],
    length: number,
    state: OverlayState,
    axisLabels: readonly string[] = GLOBAL_AXIS_LABELS
  ): void {
    const head = arrowHead(length, overlayUnit(this.objectInfo?.size));
    const up = new Vector3(0, 1, 0);

    axes.forEach((axis, a) => {
      const direction = new Vector3(axis[0], axis[1], axis[2]).normalize();
      const tip = origin.clone().addScaledVector(new Vector3(axis[0], axis[1], axis[2]), length);
      // The shaft stops mid-head, as on a load arrow.
      const headCentre = origin.clone().addScaledVector(new Vector3(axis[0], axis[1], axis[2]), length - head.length / 2);

      const material = createOverlayLineMaterial(FRAME_AXIS_COLORS[a], { state });
      material.resolution.set(this.canvasWidth, this.canvasHeight);
      this.triadLineMaterials.add(material);
      holder.add(createOverlayLine([origin.x, origin.y, origin.z, headCentre.x, headCentre.y, headCentre.z], material));

      const cone = createOverlayCone(FRAME_AXIS_COLORS[a], head, state);
      cone.position.copy(headCentre);
      cone.quaternion.setFromUnitVectors(up, direction);
      holder.add(cone);

      const labelMaterial = this.triadLabelMaterial(axisLabels[a], FRAME_AXIS_COLORS[a]);
      if (labelMaterial) {
        labelMaterial.opacity = OVERLAY_OPACITY[state];
        const label = new Sprite(labelMaterial);
        // Just past the tip, so the label does not sit on the line it names.
        label.position.copy(tip).addScaledVector(new Vector3(...axis), length * 0.12);
        label.scale.setScalar(length * 0.22);
        label.renderOrder = OVERLAY_LABEL_RENDER_ORDER;
        holder.add(label);
      }
    });
  }

  /**
   * An axis letter drawn to a canvas texture, the same way the global axis
   * helper labels its arms.
   */
  private triadLabelMaterial(text: string, color: number): SpriteMaterial | null {
    // Cached: dragging the annotation size redraws every marker many times a
    // second, and a fresh canvas plus texture upload per axis letter is the
    // expensive part of that. Materials are cheap and stay per-sprite, so
    // disposing one cannot pull the shared texture out from under another --
    // disposeTriad disposes materials only, never their maps.
    const cacheKey = `${text}|${color}`;
    const cached = this.triadLabelTextures.get(cacheKey);
    if (cached) {
      return new SpriteMaterial({ map: cached, depthTest: false, transparent: true });
    }

    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;

    // No 2d backend (headless, thumbnail capture) means no label rather than
    // a blank sprite where the letter should be.
    const context = canvas.getContext('2d');
    if (!context) return null;

    context.font = 'bold 44px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    // An outline keeps the letter readable against the model behind it.
    context.lineWidth = 6;
    context.strokeStyle = '#000000';
    context.strokeText(text, 32, 34);
    context.fillStyle = new Color(color).getStyle();
    context.fillText(text, 32, 34);

    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    this.triadLabelTextures.set(cacheKey, texture);
    return new SpriteMaterial({ map: texture, depthTest: false, transparent: true });
  }

  /**
   * Locked axes solid, free axes faint, at `origin` in model coordinates.
   * Same keyed registry as drawFrameTriad so disposal is shared.
   */
  public drawConstraintCross(
    key: string,
    origin: Vector3,
    axes: LocalFrameAxes,
    locked: boolean[],
    color: string | null
  ): void {
    this.clearFrameTriad(key);
    const root = this.attachTriadRoot();

    const length = constraintCrossHalfLength(this.objectInfo?.size);
    const holder = new Group();

    [axes.ex, axes.ey, axes.ez].forEach((axis, a) => {
      // Free axes thinner and faint, so locked and free read apart at a glance.
      // Drawn on top: the cross sits at the part's centre, inside the solid.
      const material = createOverlayLineMaterial(
        color ?? OVERLAY_COLORS.constraint,
        locked[a]
          ? { widthCssPx: OVERLAY_LINE_WIDTH_CSS_PX }
          : { widthCssPx: OVERLAY_THIN_LINE_WIDTH_CSS_PX, state: 'faint' }
      );
      material.resolution.set(this.canvasWidth, this.canvasHeight);
      this.triadLineMaterials.add(material);

      holder.add(createOverlayLine([
        origin.x - axis[0] * length, origin.y - axis[1] * length, origin.z - axis[2] * length,
        origin.x + axis[0] * length, origin.y + axis[1] * length, origin.z + axis[2] * length
      ], material));
    });

    root.add(holder);
    this.frameTriadKeys.set(key, holder);
    this.frameTriadSpecs.set(key, { kind: 'cross', origin: origin.clone(), axes, locked: [...locked], color });
  }

  /**
   * Redraws every frame marker, picking up the current annotation size. The
   * load arrows resize in place; these cannot, so the setting would otherwise
   * only reach them the next time their owner happened to redraw.
   */
  public refreshFrameTriads(): void {
    for (const [key, spec] of [...this.frameTriadSpecs]) {
      if (spec.kind === 'triad') {
        this.drawFrameTriad(key, spec.origin, spec.axes, spec.color);
      } else {
        this.drawConstraintCross(key, spec.origin, spec.axes, spec.locked, spec.color);
      }
    }
  }

  /**
   * Sample markers at the current annotation size: the force/moment/sphere
   * manipulator exactly as a load carries it, and a frame triad. The setting
   * lives on a tab where none of the real ones is usually on screen, so
   * without these the slider looks inert.
   *
   * Held in scene space and parked in front of the camera every frame rather
   * than pinned to the model, so no camera angle can put it out of view.
   */
  public showAnnotationSizePreview(): void {
    this.hideAnnotationSizePreview();

    if (!this.scene) return;

    const arrowLength = getLoadIndicatorScale(this.objectInfo.size);
    const triadLength = triadArmLength(this.objectInfo.size);

    // Divided back out of the lengths, so the two sit the same distance apart
    // at every annotation size and only grow in place.
    const step = (Math.max(arrowLength, triadLength) / (getAnnotationScale() || 1)) * 1.6;

    const holder = new Group();

    // Screen space once the holder is oriented to the camera: +X right, +Y up.
    const manipulator = this.previewManipulator(arrowLength);
    manipulator.position.set(-step * 0.5, 0, 0);
    manipulator.rotation.set(...ANNOTATION_PREVIEW_TILT);
    holder.add(manipulator);

    const triad = new Group();
    this.addPreviewTriad(triad, new Vector3(), triadLength);
    triad.rotation.set(...ANNOTATION_PREVIEW_TILT);
    triad.position.set(step * 0.5, 0, 0);
    holder.add(triad);

    this.scene.add(holder);
    this.annotationPreview = holder;
    this.updateAnnotationSizePreview();
  }

  public hideAnnotationSizePreview(): void {
    if (!this.annotationPreview) return;
    this.disposeTriad(this.annotationPreview);
    this.annotationPreview = undefined;
  }

  /**
   * Parks the samples dead centre in front of the camera. Called every frame:
   * the camera orbits, and a fixed point in the model would swing off screen.
   */
  public updateAnnotationSizePreview(): void {
    if (!this.annotationPreview || !this.camera) return;

    const forward = new Vector3();
    this.camera.getWorldDirection(forward);
    // Same depth as the model, so the camera's clipping planes contain it.
    const eye = this.camera.getWorldPosition(new Vector3());
    const distance = eye.distanceTo(this.objectInfo.center) || 1;

    this.annotationPreview.position.copy(eye).addScaledVector(forward, distance);
    this.camera.getWorldQuaternion(this.annotationPreview.quaternion);
    // An orthographic camera draws world units at its zoom, so without this
    // the samples fill the screen zoomed in and vanish zoomed out. Dividing
    // it back out keeps them one size on screen, and the slider is still the
    // only thing that changes how big they look.
    this.annotationPreview.scale.setScalar(1 / (this.camera.zoom || 1));
  }

  /**
   * The force arrow, moment and drag sphere a load carries, all on one point,
   * the way the real manipulator is built rather than three loose markers.
   */
  private previewManipulator(length: number): Group {
    const group = new Group();
    const up = new Vector3(0, 1, 0);

    group.add(this.previewArrow('line', up, length, OVERLAY_COLORS.force));
    group.add(this.previewArrow('circle', up, length, OVERLAY_COLORS.moment));

    const sphere = new Mesh(
      createOverlaySphereGeometry(LOAD_SPHERE_RADIUS),
      createOverlayMeshMaterial(OVERLAY_COLORS.remotePointSphere, 'preview')
    );
    // The real sphere is scaled the same way, from the same arrow length.
    sphere.scale.setScalar(length / LOAD_INDICATOR_DEFAULT_LENGTH);
    sphere.renderOrder = OVERLAY_RENDER_ORDER;
    group.add(sphere);

    return group;
  }

  /**
   * A sample arrow at the group's own origin. ArrowHelper builds its line and
   * cone on layer 1, which is how the real load arrows stay hidden until
   * something shows them, so the sample has to put them back on layer 0.
   */
  private previewArrow(
    shape: 'line' | 'circle',
    direction: Vector3,
    length: number,
    color: number
  ): ArrowHelper {
    const arrow = new ArrowHelper(shape, direction.clone(), new Vector3(), length, color);
    arrow.traverse((child) => child.layers.set(0));
    setOverlayState(arrow, 'preview');
    return arrow;
  }

  /** The triad arm of the preview, drawn like drawFrameTriad but translucent. */
  private addPreviewTriad(holder: Group, origin: Vector3, length: number): void {
    this.addTriadAxes(holder, origin, [[1, 0, 0], [0, 1, 0], [0, 0, 1]], length, 'preview');
  }

  /** Draws the frame the UCS tab is previewing, or nothing. */
  public updateCoordinateSystemTriads(): void {
    const key = UCS_PREVIEW_TRIAD_KEY;
    const id = this.session.previewCoordinateSystemId();
    const systems = this.session.project?.coordinateSystems ?? [];
    const index = systems.findIndex((system) => system.id === id);
    if (index === -1) {
      this.clearFrameTriad(key);
      return;
    }

    const system = systems[index];
    const points = deriveFramePoints(system.definition);
    const axes = deriveAxesFromDefinition(system.definition);
    if (!points || !axes) {
      this.clearFrameTriad(key);
      return;
    }

    this.drawFrameTriad(
      key,
      new Vector3(points.origin[0], points.origin[1], points.origin[2]),
      axes,
      coordinateSystemColor(index)
    );
  }

  private pickedPointMarkers?: PickedPointMarkers;

  /**
   * Spheres at points picked in the UCS tab, sized like local displacement
   * points. Positions are model coordinates, the same space as the frame
   * triads they sit beside. An empty list removes every marker.
   */
  public showPickedPointMarkers(markers: readonly PickedPointMarkerSpec[]): void {
    if (!this.pickedPointMarkers) {
      if (markers.length === 0) return;
      this.pickedPointMarkers = new PickedPointMarkers(() => this.attachTriadRoot());
    }
    this.pickedPointMarkers.update(markers, localPointMarkerRadius(this.objectInfo?.size));
    this.requestRender();
  }

  // ── Backward-compatible delegates to HelperManager ─────────────────
  public drawDirectionVectorChange(direction?: 'xp' | 'yp' | 'zp' | 'xm' | 'ym' | 'zm'): void { this.helpers.drawDirectionVectorChange(direction); }

  windowResize(e?: Event): void {
    if (!this.animationFired) {
      this.resizePending = true;
      return;
    }
    try {
      let canvasWidth = this.canvasWidth;
      let canvasHeight = this.canvasHeight;

      let ratio = 1;
      if (this.session.currentGraphicsSettings) {
        if (this.session.currentGraphicsSettings.UPSCALING === 1) {
          ratio = CAMERA_FRUSTUM_RATIO_WIDE;
        } else if (this.session.currentGraphicsSettings.UPSCALING === 2) {
          ratio = CAMERA_FRUSTUM_RATIO_MEDIUM;
        }
        if (this.session.currentGraphicsSettings.UPSCALING === 3) {
          ratio = CAMERA_FRUSTUM_RATIO_NARROW;
        }

        canvasWidth *= ratio;
        canvasHeight *= ratio;
      }
      this.renderScale = ratio;

      const aspect = canvasWidth / canvasHeight;
      const { size } = this.objectInfo;
      const frustumSize = Math.max(size.x, size.y, size.z);

      const width = canvasWidth > canvasHeight ? frustumSize / 2 : (frustumSize * aspect) / 2;
      const height = canvasWidth > canvasHeight ? frustumSize / aspect / 2 : frustumSize / 2;

      this.camera.right = width * 2;
      this.camera.left = -width * 2;
      this.camera.top = height * 2;
      this.camera.bottom = -height * 2;
      this.camera.updateProjectionMatrix();

      this.renderer.setSize(canvasWidth, canvasHeight, false);

      this.renderer_css2d.setSize(canvasWidth, canvasHeight);
      this.renderer_css2d.domElement.style.transform = `scale(${1 / this.pixelRatio / ratio})`;
      this.renderer_css2d.domElement.style.transformOrigin = 'top left';

      this.updateWireMaterials(canvasWidth, canvasHeight, this.pixelRatio * ratio);
      this.triadLineMaterials.forEach((material) => material.resolution.set(canvasWidth, canvasHeight));
      this.requestRender();
    } catch (e) {}
  }

  /**
   * Render the current view at high resolution (default 3840px long edge — 4K)
   * and return it as a PNG data URL. Only the drawing buffer is resized (CSS
   * size untouched, `updateStyle: false`), and both dimensions scale by the
   * same factor so the orthographic frustum needs no change.
   *
   * The frame is supersampled over `SCREENSHOT_SAMPLES` sub-pixel camera
   * offsets (see renderSupersampled). The result is on the canvas right after,
   * so it is read synchronously: the renderer is created without
   * `preserveDrawingBuffer`. The view gizmo and CSS2D annotations are
   * intentionally not rendered into the capture.
   */
  captureScreenshot(targetLongEdge = 3840): string | null {
    if (!this.renderer || !this.camera || !this.scene) return null;
    const { renderer, camera, scene } = this;

    const prevPixelRatio = renderer.getPixelRatio();
    const prevSize = renderer.getSize(new Vector2());
    const longCssEdge = Math.max(prevSize.x, prevSize.y);
    if (longCssEdge === 0) return null;

    // Never downscale below the current buffer, never exceed what the GPU
    // can allocate as a render target.
    const maxTextureSize = renderer.capabilities.maxTextureSize ?? 4096;
    const currentBufferLongEdge = longCssEdge * prevPixelRatio;
    const longEdge = Math.min(
      Math.max(targetLongEdge, currentBufferLongEdge),
      maxTextureSize,
    );
    const scale = longEdge / longCssEdge;
    const width = Math.round(prevSize.x * scale);
    const height = Math.round(prevSize.y * scale);

    try {
      renderer.setPixelRatio(1);
      renderer.setSize(width, height, false);
      // Edges keep their on-screen weight: scale them with the capture.
      const cssWidth = this.canvas.clientWidth || prevSize.x;
      this.updateWireMaterials(width, height, width / cssWidth);

      // Mirror the animation loop's render path (autoClear is off).
      renderSupersampled(renderer, width, height, SCREENSHOT_SAMPLES, (offsetX, offsetY) => {
        camera.setViewOffset(width, height, offsetX, offsetY, width, height);
        renderer.clear();
        renderer.render(scene, camera);
      });

      return renderer.domElement.toDataURL('image/png');
    } catch (e) {
      console.error('[Engine] captureScreenshot failed:', e);
      return null;
    } finally {
      camera.clearViewOffset();
      renderer.setPixelRatio(prevPixelRatio);
      renderer.setSize(prevSize.x, prevSize.y, false);
      this.updateWireMaterials(prevSize.x, prevSize.y, this.pixelRatio * this.renderScale);
      this.requestRender();
    }
  }

  // ── Render Studio ──────────────────────────────────────────────────
  /** While set, the animation loop draws nothing: the Render Studio has the scene staged. */
  private renderSuspended = false;

  /**
   * The engine as the Render Studio sees it (cloud admins only). Only
   * model surfaces count: input part meshes, optimized and result meshes, FEA
   * field meshes and soft jaws. Load markers, helpers, grids and labels are
   * hidden by the studio for the render.
   */
  public renderStudioHost(): RenderStudioHost {
    const isShown = (object: Object3D): boolean => {
      for (let o: Object3D | null = object; o; o = o.parent) {
        if (!o.visible) return false;
      }
      return true;
    };
    const roots = (): Object3D[] =>
      [this.group, this.optimizedGroup, this.optimizedGroupParametric, this.infoGroup, this.jaws.group, this.jaws.result]
        .filter((root) => root.parent !== null);

    return {
      renderer: this.renderer,
      scene: this.scene,
      camera: this.camera,
      worldUp: Object3D.DEFAULT_UP.clone(),
      envMap: this.envMap,
      modelMeshes: (): Mesh[] => {
        const meshes: Mesh[] = [];
        for (const root of roots()) {
          root.traverseVisible((object) => {
            const mesh = object as Mesh & { isLine2?: boolean; isLineSegments2?: boolean };
            if (!mesh.isMesh || mesh.isLine2 || mesh.isLineSegments2) return;
            // Inside the input model only the part meshes count, not frame markers.
            if (root === this.group && mesh.userData['isMergedMesh'] !== true) return;
            if (isShown(mesh)) meshes.push(mesh);
          });
        }
        return meshes;
      },
      partOf: (mesh: Mesh): RenderPart | null => {
        const isUnder = (root: Object3D): boolean => {
          for (let o: Object3D | null = mesh.parent; o; o = o.parent) if (o === root) return true;
          return false;
        };
        // Result parts come back named by the input part's id or name.
        const partName = (idOrName: string): string =>
          this.objects.find((o) => o.data.id === idOrName || o.data.name === idOrName)?.data.name || idOrName;
        if (mesh.userData['isMergedMesh'] === true) {
          const id = String(mesh.userData['id']);
          return { key: `part:${id}`, label: partName(id) || 'Part' };
        }
        if (mesh === this.jaws.fixed.mesh) return { key: 'jaw:fixed', label: 'Fixed jaw' };
        if (mesh === this.jaws.movable.mesh) return { key: 'jaw:movable', label: 'Movable jaw' };
        const name = mesh.parent?.name ?? '';
        if (isUnder(this.jaws.result)) return { key: `jaw-result:${name}`, label: `${partName(name) || 'Part'} · soft jaws` };
        if (isUnder(this.optimizedGroup) || isUnder(this.optimizedGroupParametric)) {
          return { key: `result:${name}`, label: `${partName(name) || 'Part'} · result` };
        }
        return null;
      },
      isOrganic: (mesh: Mesh): boolean => {
        if (mesh.name !== 'OptimizedOrganicMesh') return false;
        // Result parts come back named by the input part's id or name; the others are the fixed CAD parts around them.
        const designNames = new Set(this.objectsDesign.flatMap((o) => [o.data.id, o.data.name]));
        let under = false;
        let design = false;
        for (let o: Object3D | null = mesh; o; o = o.parent) {
          if (o === this.optimizedGroup) under = true;
          else if (designNames.has(o.name)) design = true;
        }
        if (!under) return false;
        if (design) return true;
        // No result part matched to a design part: the whole result is the optimized shape, as morphModels takes it.
        let matched = false;
        this.optimizedGroup.traverse((o) => {
          if (designNames.has(o.name)) matched = true;
        });
        return !matched;
      },
      modelEdges: (): Object3D[] => {
        const edges: Object3D[] = [];
        for (const root of roots()) {
          root.traverseVisible((object) => {
            if (object.userData['line_type'] !== undefined && isShown(object)) edges.push(object);
          });
        }
        for (const jaw of [this.jaws.fixed.edges, this.jaws.movable.edges]) {
          if (jaw && isShown(jaw)) edges.push(jaw);
        }
        return edges;
      },
      morphModels: (): MorphModels | null => {
        // The result Results shows: prismatic when picked there, organic otherwise.
        const hasResult = (group: Group): boolean => group.parent !== null && group.children.length > 0;
        const resultGroup =
          hasResult(this.optimizedGroupParametric) && this.optimizedGroupParametric.visible
            ? this.optimizedGroupParametric
            : hasResult(this.optimizedGroup)
              ? this.optimizedGroup
              : hasResult(this.optimizedGroupParametric)
                ? this.optimizedGroupParametric
                : null;
        if (!resultGroup) return null;
        // Result parts come back named by the input part's id or name.
        const designNames = new Set(this.objectsDesign.flatMap((o) => [o.data.id, o.data.name]));
        const isDesignResult = (object: Object3D): boolean => {
          for (let o: Object3D | null = object; o && o !== resultGroup; o = o.parent) if (designNames.has(o.name)) return true;
          return false;
        };
        const result: Mesh[] = [];
        const fixed: Mesh[] = [];
        const fixedEdges: Object3D[] = [];
        resultGroup.traverse((object) => {
          // Clipping adds its own cap and back-side meshes beside the result's.
          if (object instanceof Mesh && (object.name === 'OptimizedOrganicMesh' || object.name === 'OptimizedPrismaticMesh')) {
            (isDesignResult(object) ? result : fixed).push(object);
          } else if (object.userData['line_type'] !== undefined && !isDesignResult(object)) {
            fixedEdges.push(object);
          }
        });
        // Design-space parts the user hasn't hidden; with no result part matched to one, the whole input morphs into the whole result.
        const matched = result.length > 0;
        const input: Mesh[] = [];
        for (const obj of matched ? this.objectsDesign : this.objects) {
          if (!obj.group.visible) continue;
          obj.group.traverse((object) => {
            if (object instanceof Mesh && object.userData['isMergedMesh'] === true) input.push(object);
          });
        }
        if (!matched) {
          result.push(...fixed.splice(0));
          fixedEdges.length = 0;
        }
        return result.length > 0 && input.length > 0 ? { input, result, fixed, fixedEdges } : null;
      },
      viewportCssSize: (): { width: number; height: number } => ({
        width: this.canvas.clientWidth,
        height: this.canvas.clientHeight
      }),
      setSuspended: (suspended: boolean): void => {
        this.renderSuspended = suspended;
        if (!suspended) this.requestRender();
      },
      scaleEdges: (width: number, height: number, bufferPxPerCssPx: number): void =>
        this.updateWireMaterials(width, height, bufferPxPerCssPx),
      restoreEdges: (): void => {
        const size = this.renderer.getSize(new Vector2());
        this.updateWireMaterials(size.x, size.y, this.pixelRatio * this.renderScale);
      },
      overlayDepthOffset: (): number => OVERLAY_DEPTH_BIAS_FRACTION * this.modelMaxDimension,
      setOverlayDepthBias: (ndc: number): void => {
        this.inputDepthBias.value = this.overlayVisible() ? ndc : 0;
        this.translucentDepthBias.value = ndc;
      }
    };
  }

  async decodeAssemblyProto(buffer: ArrayBuffer): Promise<ProtoAssembly> {
    return this.assemblyManager.decodeAssemblyProto(buffer);
  }
  async setAssembly(assembly: ArrayBuffer): Promise<void> { return this.assemblyManager.setAssembly(assembly); }
  async setOptimizedAssembly(assembly: ArrayBuffer): Promise<void> { return this.assemblyManager.setOptimizedAssembly(assembly); }
  async setOptimizedAssemblyParametric(assembly: ArrayBuffer): Promise<void> { return this.assemblyManager.setOptimizedAssemblyParametric(assembly); }
  setOptimizedInfo(info: FEAResultInfoV2 | FEAResultMeshSet[]): void { this.assemblyManager.setOptimizedInfo(info); }

  public vertexShader = FEA_VERTEX_SHADER;
  public fragmentShader = FEA_FRAGMENT_SHADER;

  public optimizeInfoFlowVersion2: FEAResultInfoV2 = { organic: [] };

  /**
   * Helper to get geometry worker service
   */
  private get geometryWorker(): GeometryWorkerService {
    return this.session.geometryWorker;
  }

  /**
   * Compute BVH for a geometry using web worker.
   * Falls back to synchronous computation if worker is unavailable.
   */
  async computeBVHAsync(geometry: BufferGeometry, options?: { targetLeafSize?: number }): Promise<void> {
    const positionAttr = geometry.getAttribute('position');
    const indexAttr = geometry.getIndex();

    if (!positionAttr || !indexAttr) {
      // Fallback to sync for non-indexed geometry
      geometry.computeBoundsTree();
      return;
    }

    // Try worker first
    if (this.geometryWorker?.isSupported) {
      try {
        const positions = positionAttr.array as Float32Array;
        const indices = indexAttr.array as Uint32Array;

        const result = await this.geometryWorker.computeBVH(positions, indices, {
          targetLeafSize: options?.targetLeafSize ?? BVH_TARGET_LEAF_SIZE
        });

        // Deserialize and attach to geometry
        const bvh = MeshBVH.deserialize(result.serializedBVH, geometry);
        geometry.boundsTree = bvh;
        return;
      } catch (e) {
        console.warn('Worker BVH computation failed, falling back to sync:', e);
      }
    }

    // Fallback to synchronous computation
    geometry.computeBoundsTree();
  }

  /**
   * Convert ProcessedGeometry from worker to Three.js BufferGeometry
   */
  private processedGeometryToBuffer(processed: ProcessedGeometry): BufferGeometry {
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(processed.positions, 3));
    geometry.setIndex(new BufferAttribute(processed.indices, 1));
    geometry.setAttribute('normal', new BufferAttribute(processed.normals, 3));
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();
    return geometry;
  }

  /**
   * Process a single face geometry - uses worker if available, falls back to main thread
   */
  async processFaceGeometry(
    vertices: Float32Array | number[],
    indices: Uint32Array | number[],
    cutOffAngle: number = WIRE_CUTOFF_ANGLE
  ): Promise<BufferGeometry> {
    // Try worker first
    if (this.geometryWorker?.isSupported) {
      try {
        const processed = await this.geometryWorker.processGeometry(
          vertices instanceof Float32Array ? vertices : new Float32Array(vertices),
          indices instanceof Uint32Array ? indices : new Uint32Array(indices),
          cutOffAngle
        );
        return this.processedGeometryToBuffer(processed);
      } catch (e) {
        console.warn('Worker geometry processing failed, falling back to main thread:', e);
      }
    }

    // Fallback to main thread
    const baseGeometry = new BufferGeometry();
    baseGeometry.setAttribute(
      'position',
      new BufferAttribute(vertices instanceof Float32Array ? vertices : new Float32Array(vertices), 3)
    );
    baseGeometry.setIndex(
      new BufferAttribute(indices instanceof Uint32Array ? indices : new Uint32Array(indices), 1)
    );

    // three's mergeVertices and EdgeSplitModifier both key their hash maps on
    // per-vertex strings, so on a large imported mesh they exhaust the heap —
    // and here that happens on the main thread, freezing the tab rather than
    // just a worker. Past the budget, take the same cheap path the worker does
    // and only compute normals. See MAIN_THREAD_MESH_PROCESSING_MAX_TRIANGLES.
    const triangleCount = (baseGeometry.getIndex()?.count ?? 0) / 3;
    if (triangleCount > MAIN_THREAD_MESH_PROCESSING_MAX_TRIANGLES) {
      console.warn(
        `Skipping main-thread weld/edge-split for ${triangleCount} triangles ` +
        `(budget ${MAIN_THREAD_MESH_PROCESSING_MAX_TRIANGLES}); rendering with smooth normals.`
      );
      baseGeometry.computeVertexNormals();
      baseGeometry.computeBoundingSphere();
      baseGeometry.computeBoundingBox();
      return baseGeometry;
    }

    const edgeSplit = new EdgeSplitModifier();
    const cutOffRad = (cutOffAngle * Math.PI) / 180;
    const geometry = mergeVertices(edgeSplit.modify(baseGeometry, cutOffRad, false));
    geometry.computeVertexNormals();
    geometry.computeBoundingSphere();
    geometry.computeBoundingBox();
    return geometry;
  }

  createWireLineMaterial(): LineMaterial {
    const material = new LineMaterial({
      color: new Color('rgb(0,0,0)'),
      linewidth: this.wireLineWidth(false),
      dashed: false,
      precision: 'highp',
      polygonOffset: true,
      polygonOffsetFactor: POLYGON_OFFSET_FACTOR,
      clippingPlanes: this.clippingPlanes
    });
    material.resolution.set(
      this.canvasWidth * this.renderScale,
      this.canvasHeight * this.renderScale
    );
    this.wireMaterials.add(material);
    material.addEventListener('dispose', () => this.wireMaterials.delete(material));
    return material;
  }

  // ── Edge line weight ───────────────────────────────────────────────
  // LineMaterial widths are in drawing-buffer pixels. Edges are specified in
  // CSS pixels and converted here, so they read the same at any device pixel
  // ratio and graphics-quality render scale.
  private readonly wireMaterials = new Set<LineMaterial>();
  /** Graphics-quality render scale (1, 0.75, 0.5) the drawing buffer is sized with. */
  private renderScale = 1;

  /** Buffer-pixel width of a part edge, normal or selected. Never below one pixel. */
  public wireLineWidth(selected: boolean, bufferPxPerCssPx = this.pixelRatio * this.renderScale): number {
    const cssWidth = selected ? WIRE_SELECTED_LINE_WIDTH_CSS_PX : WIRE_LINE_WIDTH_CSS_PX;
    return Math.max(1, cssWidth * bufferPxPerCssPx);
  }

  /** Marks a part's edges as selected (thicker) or not. */
  public setWireSelected(material: LineMaterial, selected: boolean): void {
    material.userData['wireSelected'] = selected;
    material.linewidth = this.wireLineWidth(selected);
    this.requestRender();
  }

  /**
   * Buffer-pixel width of a line material: its own `lineWidthCssPx` when it has
   * one, otherwise the part-edge width for its selection state.
   */
  public materialLineWidth(material: LineMaterial, bufferPxPerCssPx = this.pixelRatio * this.renderScale): number {
    const cssWidth = material.userData['lineWidthCssPx'];
    if (typeof cssWidth === 'number') return Math.max(1, cssWidth * bufferPxPerCssPx);
    return this.wireLineWidth(material.userData['wireSelected'] === true, bufferPxPerCssPx);
  }

  private updateWireMaterials(width: number, height: number, bufferPxPerCssPx: number): void {
    for (const material of this.wireMaterials) {
      material.resolution.set(width, height);
      material.linewidth = this.materialLineWidth(material, bufferPxPerCssPx);
    }
  }

  createMergedWireGroup(
    wires: ProtoWire[],
    wirematerial: LineMaterial,
    lineType: string
  ): { wiresGroup: Group; wireNames: string[] } {
    const wiresGroup = new Group();
    wiresGroup.name = 'wires';
    const wireNames: string[] = [];
    const allSegmentPositions: number[] = [];

    for (const wireValues of wires) {
      const wire = wireValues.geometry;
      for (let j = 0; j < wire.length - 3; j += 3) {
        const x0 = wire[j], y0 = wire[j + 1], z0 = wire[j + 2];
        const x1 = wire[j + 3], y1 = wire[j + 4], z1 = wire[j + 5];
        if (Math.abs(x0) > 1e38 || Math.abs(y0) > 1e38 || Math.abs(z0) > 1e38 ||
            Math.abs(x1) > 1e38 || Math.abs(y1) > 1e38 || Math.abs(z1) > 1e38) continue;
        allSegmentPositions.push(x0, y0, z0, x1, y1, z1);
      }
    }

    if (allSegmentPositions.length > 0) {
      const mergedWireGeometry = new LineSegmentsGeometry();
      mergedWireGeometry.setPositions(allSegmentPositions);
      const mergedLine = new LineSegments2(mergedWireGeometry, wirematerial);
      mergedLine.computeLineDistances();
      mergedLine.userData['line_type'] = lineType;
      mergedLine.scale.set(1, 1, 1);
      mergedLine.name = mergedLine.uuid;
      wireNames.push(mergedLine.name);
      wiresGroup.add(mergedLine);
    }

    return { wiresGroup, wireNames };
  }

  async initMesh(): Promise<void> {
    this.progressUpdate.next({ total: this.assembly.parts.length, current: 1 });
    const cutOffAngle = WIRE_CUTOFF_ANGLE;

    // Process all parts - use worker pool for parallel processing if available
    const processPartAsync = async (obj: ProtoPart, partIndex: number) => {
      const group = new Group();
      group.name = obj.name;
      group.userData = { id: obj.id, pid: obj.parentId };

      // Process all face geometries in parallel using worker
      const faceGeometries: BufferGeometry[] = [];
      const faceIds: string[] = [];

      // Process faces in parallel batches
      const facePromises = obj.faces.map(async (face: ProtoFace) => {
        const geometry = await this.processFaceGeometry(
          new Float32Array(face.vertices),
          new Uint32Array(face.indices),
          cutOffAngle
        );
        return { geometry, faceId: face.id };
      });

      const processedFaces = await Promise.all(facePromises);
      for (const { geometry, faceId } of processedFaces) {
        faceGeometries.push(geometry);
        faceIds.push(faceId);
      }
      // Merge all face geometries into one with faceIndex attribute
      const { geometry: mergedGeometry, faceIdToIndex } = mergeGeometriesWithFaceIndex(faceGeometries, faceIds);
      const partColor = readablePartColor(obj.color);

      // Create per-face material that extends MeshPhysicalMaterial
      const perFaceMaterial = new PerFaceMaterial(
        {
          color: partColor,
          side: DoubleSide,
          vertexColors: false,
          flatShading: false,
          opacity: 1,
          transparent: false,
          metalness: PART_METALNESS,
          reflectivity: PART_REFLECTIVITY,
          roughness: PART_ROUGHNESS,
          clearcoat: PART_CLEARCOAT,
          envMap: this.envMap,
          envMapIntensity: PART_ENVMAP_INTENSITY,
          clippingPlanes: this.clippingPlanes,
          ...FACE_POLYGON_OFFSET
        },
        faceIds,
        partColor
      );
      perFaceMaterial.depthBias = this.inputDepthBias;
      perFaceMaterial.translucentDepthBias = this.translucentDepthBias;

      // Create single mesh for all faces
      const meshobj = new Mesh(mergedGeometry, perFaceMaterial);
      // Compute BVH asynchronously in worker
      this.computeBVHAsync(meshobj.geometry).catch(() => {});
      meshobj.name = meshobj.uuid;
      meshobj.userData = {
        id: obj.id,
        parentId: obj.parentId,
        originalColor: partColor,
        faceIds: faceIds,
        faceIdToIndex: faceIdToIndex,
        perFaceMaterial: perFaceMaterial,
        isMergedMesh: true
      };

      group.add(meshobj);

      // Store reference to faceIds for selector compatibility
      const faces = faceIds;

      const wirematerial = this.createWireLineMaterial();

      const { wiresGroup, wireNames: wires } = this.createMergedWireGroup(obj.wires, wirematerial, 'AssemblyMeshLine');
      group.add(wiresGroup);

      this.createPartHelpers(group);

      return {
        group,
        data: obj,
        faces,
        wires,
        wirematerial
      };
    };

    // Parts are processed with several in flight at once.
    //
    // This used to be a plain sequential `await` per part, on the reasoning
    // that faces within a part already run in parallel. In practice that
    // starved the worker pool: a typical part has only a handful of faces, so
    // an assembly of N parts submitted ~5 tasks, waited for all of them, then
    // submitted the next ~5 — leaving most of the pool's 8 workers idle for
    // the whole load. Keeping a few parts in flight keeps every worker fed.
    //
    // Ordering is still deterministic: results are written to a
    // pre-allocated slot by index and appended in index order afterwards, so
    // `objects`/`groups` come out exactly as the sequential loop produced them.
    const parts = this.assembly.parts;
    const total = parts.length;
    const partResults = new Array<Awaited<ReturnType<typeof processPartAsync>>>(total);
    const inFlight = Math.max(1, Math.min(this.geometryWorker?.poolCapacity ?? 4, total));
    let nextIndex = 0;
    let completed = 0;

    const runPartWorker = async (): Promise<void> => {
      for (;;) {
        const i = nextIndex++;
        if (i >= total) return;
        partResults[i] = await processPartAsync(parts[i], i);
        this.progressUpdate.next({ total, current: ++completed });
      }
    };

    await Promise.all(Array.from({ length: inFlight }, runPartWorker));

    for (const partData of partResults) {
      this.group.add(partData.group);
      this.groups.push(partData.group);
      this.objects.push(partData);
    }

    this.objects.sort(function (a, b) {
      if (a.data.name < b.data.name) {
        return -1;
      }
      if (a.data.name > b.data.name) {
        return 1;
      }
      return 0;
    });

    // Apply custom part names if they exist
    if (this.session.project?.partNames) {
      this.updatePartNames(this.session.project.partNames);
    }

    this.scene.add(this.group);
  }

  private createPartHelpers(group: Group): void {
    const createArrow = (
      name: string,
      type: 'line' | 'circle',
      dir: Vector3,
      color: number,
      center: Vector3
    ): ArrowHelper => {
      const start = new Vector3(0, 0, LOAD_INDICATOR_DEFAULT_LENGTH);
      const end = new Vector3(0, 0, 0);
      const direction = start.clone().sub(end);
      const length = direction.length();
      const arrow = new ArrowHelper(type, direction.normalize(), end, length, color);
      arrow.setDirection(dir);
      arrow.name = name;
      arrow.position.copy(center);
      arrow.layers.set(1);
      this.partHelperGroup.add(arrow);
      return arrow;
    };

    const createLinkLine = (to: Vector3, from: Vector3, color: number = OVERLAY_COLORS.link): Line2 => {
      const matLine = createOverlayLineMaterial(color, { widthCssPx: OVERLAY_THIN_LINE_WIDTH_CSS_PX, dashed: true });
      const line = createOverlayLine([from.x, from.y, from.z, to.x, to.y, to.z], matLine);
      line.layers.set(1);
      this.partHelperGroup.add(line);
      return line;
    };

    const createDragControl = (
      name: string,
      color: number,
      center: Vector3,
      partArrow: ArrowHelper,
      partMoment: ArrowHelper,
      partLink: Line2
    ): Mesh => {
      const sphere = new Mesh(createOverlaySphereGeometry(LOAD_SPHERE_RADIUS), createOverlayMeshMaterial(color));
      sphere.renderOrder = OVERLAY_RENDER_ORDER;
      sphere.name = name;
      sphere.position.copy(center);
      sphere.layers.set(1);
      this.partHelperGroup.add(sphere);

      const controls = new DragControls([sphere], this.camera, this.canvas);
      controls.enabled = false;

      controls.addEventListener('hoveron', (event) => {
        this.selector.isMouseMoveHalted = true;
        this.selector.unhighlightPart();
        setOverlayState(event.object, 'hover');
        this.requestRender();
      });

      controls.addEventListener('hoveroff', (event) => {
        this.selector.isMouseMoveHalted = false;
        setOverlayState(event.object, 'normal');
        this.requestRender();
      });

      controls.addEventListener('dragstart', (event) => {
        this.selector.isMouseDownHalted = true;
        this.selector.unhighlightPart();
        // Every drag tick writes the load's remote point; save once when the drag ends.
        this.session.holdAutoSave(REMOTE_POINT_DRAG_HOLD);
        // Both spheres can sit under the same pointer; whichever control captures
        // first must suppress the other or one pointer drags both spheres at once.
        const referenceControl = (group.userData['part_reference_sphere'] as Mesh | undefined)
          ?.userData['dragControl'] as DragControls | undefined;
        if (referenceControl) referenceControl.enabled = false;
        setOverlayState(event.object, 'hover');
      });

      controls.addEventListener('drag', (event) => {
        if (partArrow && partMoment) {
          partArrow.position.copy(event.object.position);
          partMoment.position.copy(event.object.position);
          const lineGeometry = new LineGeometry();
          // The link sits in the part helper holder with the sphere, in model coordinates.
          const ctr = new Box3().setFromObject(group).getCenter(new Vector3());
          lineGeometry.setPositions([
            ctr.x, ctr.y, ctr.z,
            event.object.position.x, event.object.position.y, event.object.position.z
          ]);
          partLink?.geometry.copy(lineGeometry);
          partLink?.computeLineDistances();
          partLink?.scale.set(1, 1, 1);
        }
        this.selector.moved.emit(event.object.position);
      });

      controls.addEventListener('dragend', (event) => {
        this.selector.isMouseDownHalted = false;
        setOverlayState(event.object, 'normal');
        this.requestRender();
        this.selector.moved.emit(event.object.position);
        this.session.releaseAutoSave(REMOTE_POINT_DRAG_HOLD);
      });

      sphere.userData['dragControl'] = controls;
      return sphere;
    };

    const createReactionForceAnnotation = (
      name: string,
      center: Vector3
    ): {
      annotation: CSS2DObject;
      setAnnotationText: (x: string, y: string, z: string, magnitude: string, unit: string) => void;
    } => {
      const div = document.createElement('div');
      div.className = 'annotation-container';
      const annotation = new CSS2DObject(div);
      annotation.name = name;
      annotation.position.copy(center);
      annotation.visible = false;
      this.ucsFrame.add(annotation);

      const setAnnotationText = (x: string, y: string, z: string, magnitude: string, unit: string): void => {
        div.textContent = '';
        const container = document.createElement('div');
        container.className = 'annotation-container';
        const content = document.createElement('div');
        content.className = 'annotation-dimension-content reaction-force-annotation';
        content.style.display = 'flex';
        content.style.flexDirection = 'column';
        content.style.gap = '1px';
        // Magnitude on top — what the user reads first
        const magSpan = document.createElement('span');
        magSpan.style.fontWeight = '600';
        magSpan.textContent = `|F| = ${magnitude} ${unit}`;
        content.appendChild(magSpan);
        // Components below
        for (const [label, val] of [['X', x], ['Y', y], ['Z', z]]) {
          const span = document.createElement('span');
          span.textContent = `${label}: ${val} ${unit}`;
          content.appendChild(span);
        }
        container.appendChild(content);
        div.appendChild(container);
      };
      return { annotation, setAnnotationText };
    };

    // Reaction-force arrow: a 3D arrow at the part center pointing in the
    // reaction-force direction, length scaled by magnitude. Created hidden;
    // visibility + direction + length are set when the toggle fires.
    // Amber, apart from the applied force and moment colours.
    const createReactionForceArrow = (name: string, center: Vector3): ArrowHelper => {
      const arrowLength = LOAD_INDICATOR_DEFAULT_LENGTH;
      const arrow = new ArrowHelper('line', new Vector3(0, 0, 1), new Vector3(), arrowLength, OVERLAY_COLORS.reactionForce);
      arrow.name = name;
      arrow.position.copy(center);
      arrow.visible = false;
      // ArrowHelper constructor puts line + cone on layer 1 (hidden). Override
      // both children to layer 0 so the camera renders them once visible=true.
      arrow.layers.set(0);
      arrow.line.layers.set(0);
      arrow.cone.layers.set(0);
      this.ucsFrame.add(arrow);
      return arrow;
    };

    group.updateWorldMatrix(true, true);
    group.updateMatrixWorld(true);
    group.updateMatrix();

    const center = new Box3().setFromObject(group).getCenter(new Vector3());
    center.applyMatrix4(group.matrixWorld.clone().invert());
    group.localToWorld(center);

    group.userData['part_arrow'] = createArrow('part_arrow', 'line', new Vector3(0, 0, 0), OVERLAY_COLORS.force, center);
    group.userData['part_moment'] = createArrow('part_moment', 'circle', new Vector3(0, 0, 0), OVERLAY_COLORS.moment, center);
    group.userData['part_link_line'] = createLinkLine(center, center, OVERLAY_COLORS.link);
    group.userData['part_sphere'] = createDragControl(
      'part_sphere', OVERLAY_COLORS.remotePointSphere, center,
      group.userData['part_arrow'],
      group.userData['part_moment'],
      group.userData['part_link_line']
    );

    // Remote-mass markers (vibration-family remotePoint mode): labels for both
    // points and a draggable reference-point sphere. Created hidden; the
    // selector's show/hide/set RemoteMassMarkers methods drive them.
    const createSphereLabel = (name: string, label: string, parent: Object3D): CSS2DObject => {
      const div = document.createElement('div');
      div.className = 'annotation-box-text';
      const content = document.createElement('div');
      content.className = 'annotation-content';
      content.textContent = label;
      div.appendChild(content);
      const annotation = new CSS2DObject(div);
      annotation.name = name;
      annotation.position.set(0, 0, -LOAD_SPHERE_RADIUS * 1.5);
      annotation.visible = false;
      parent.add(annotation);
      return annotation;
    };

    const createReferenceSphere = (name: string, color: number, refCenter: Vector3): Mesh => {
      const sphere = new Mesh(createOverlaySphereGeometry(LOAD_SPHERE_RADIUS), createOverlayMeshMaterial(color));
      sphere.renderOrder = OVERLAY_RENDER_ORDER;
      sphere.name = name;
      sphere.position.copy(refCenter);
      sphere.layers.set(1);
      this.partHelperGroup.add(sphere);

      const controls = new DragControls([sphere], this.camera, this.canvas);
      controls.enabled = false;

      controls.addEventListener('hoveron', (event) => {
        this.selector.isMouseMoveHalted = true;
        this.selector.unhighlightPart();
        setOverlayState(event.object, 'hover');
        this.requestRender();
      });
      controls.addEventListener('hoveroff', (event) => {
        this.selector.isMouseMoveHalted = false;
        setOverlayState(event.object, 'normal');
        this.requestRender();
      });
      controls.addEventListener('dragstart', () => {
        this.selector.isMouseDownHalted = true;
        this.selector.unhighlightPart();
        this.session.holdAutoSave(REFERENCE_POINT_DRAG_HOLD);
        // Mirror of the part-sphere suppression: one pointer must never drag both spheres
        const partControl = (group.userData['part_sphere'] as Mesh | undefined)
          ?.userData['dragControl'] as DragControls | undefined;
        if (partControl) partControl.enabled = false;
      });
      controls.addEventListener('drag', (event) => {
        this.selector.referenceMoved.emit(event.object.position);
      });
      controls.addEventListener('dragend', (event) => {
        this.selector.isMouseDownHalted = false;
        setOverlayState(event.object, 'normal');
        this.requestRender();
        // Restore the remote sphere's drag (it is always draggable in remote-mass edit mode)
        const partControl = (group.userData['part_sphere'] as Mesh | undefined)
          ?.userData['dragControl'] as DragControls | undefined;
        if (partControl) partControl.enabled = true;
        this.selector.referenceMoved.emit(event.object.position);
        this.session.releaseAutoSave(REFERENCE_POINT_DRAG_HOLD);
      });

      sphere.userData['dragControl'] = controls;
      return sphere;
    };

    group.userData['part_sphere_annotation'] = createSphereLabel(
      'part_sphere_annotation', 'Remote Mass', group.userData['part_sphere']
    );
    group.userData['part_reference_sphere'] = createReferenceSphere('part_reference_sphere', OVERLAY_COLORS.referencePoint, center);
    group.userData['part_reference_annotation'] = createSphereLabel(
      'part_reference_annotation', 'Reference Point', group.userData['part_reference_sphere']
    );
    group.userData['part_reference_link_line'] = createLinkLine(center, center, OVERLAY_COLORS.referencePoint);
    group.userData['part_reaction_force_annotation'] = createReactionForceAnnotation(
      'part_reaction_force_annotation', center
    );
    group.userData['part_reaction_force_arrow'] = createReactionForceArrow(
      'part_reaction_force_arrow', center
    );
  }

  initOptimizedMesh() {
    this.optimizedGroup.visible = true;
    this.optimizedGroup.add(this.assemblyOptimized);
    this.scene.add(this.optimizedGroup);
    this.alignResultsToUcs();
  }

  /**
   * The CAD result meshes arrive in the model's own orientation — the
   * pipeline turns the result STEP back — and the FEA field meshes in the
   * UCS frame, so the field meshes get the UCS turn undone; after an FEA
   * run, which solves the model as imported, they arrive as imported too. In print
   * orientation the manager turns results and FEA field meshes together.
   */
  public alignResultsToUcs(): void {
    this.printOrientation.align();
  }

  /** The turn a UCS gives the model on screen; identity when the UCS is off. */
  public ucsTurn(): Matrix4 {
    return ucsDisplayMatrix(this.session.project.userCoordinateSystem);
  }

  /** The input model and symmetry planes, which stay in the input orientation. */
  public inputModelObjects(): Object3D[] {
    const { xy, xz, yz } = this.symmetryPlanes;
    return [this.group, xy, xz, yz].filter((object): object is Group => !!object);
  }

  updatePartNames(partNames: { [partId: string]: string }) {
    this.objects.forEach((obj) => {
      if (partNames[obj.data.id]) {
        obj.data.name = partNames[obj.data.id];
        obj.group.name = partNames[obj.data.id];
      }
    });
  }

  initOptimizedMeshParametric() {
    this.optimizedGroupParametric.visible = false;
    this.optimizedGroupParametric.add(this.assemblyOptimizedParametric);
    this.scene.add(this.optimizedGroupParametric);
    this.alignResultsToUcs();
  }

  // -- Drill bit delegates (implementation in DrillBitsManager) --
  generateSymmetryPlanes(maxSize: number, center: Vector3, size: Vector3): void { this.drillBitsManager.generateSymmetryPlanes(maxSize, center, size); }
  disposeDrillBits(): void { this.drillBitsManager.disposeDrillBits(); }
  disposeSymmetryPlanes(): void { this.drillBitsManager.disposeSymmetryPlanes(); }
  showMillingDepthPlanes(resolve: MillingDepthPlaneResolver | null): void { this.drillBitsManager.showMillingDepthPlanes(resolve); }
  async initDrillBits(): Promise<void> { return this.drillBitsManager.initDrillBits(); }
  addDrillBitsToOutline(): void { this.drillBitsManager.addDrillBitsToOutline(); }
  removeDrillBitsFromOutline(): void { this.drillBitsManager.removeDrillBitsFromOutline(); }
  drillBitUpdate(): void { this.drillBitsManager.drillBitUpdate(); }
  scaleMeshToDiameter(mesh: Mesh, desiredDiameter: number): number { return this.drillBitsManager.scaleMeshToDiameter(mesh, desiredDiameter); }
  drillBitScaleChange(diameter: number): void { this.drillBitsManager.drillBitScaleChange(diameter); }

  createAndAnimateArrow(s: Vector3, d: Vector3, b: Box3, dist: number): void { this.helpers.createAndAnimateArrow(s, d, b, dist); }
  showArrow(): void { this.helpers.showArrow(); }
  hideArrow(): void { this.helpers.hideArrow(); }
  disposeArrow(): void { this.helpers.disposeArrow(); }

  private fpsMonitor = new FPSMonitor();
  private themeSubscription?: Subscription;
  private zoneStableSubscription?: Subscription;
  private lastActivityTime = 0;
  private lastIdleRenderTime = 0;
  private renderIdle = false;
  private paused = false;
  /** True once releaseGpu() has dropped the WebGL context; nothing may restart the loop after this. */
  private released = false;
  /** True once dispose() has run; the scene and the part list are gone. */
  private _disposed = false;

  get disposed(): boolean {
    return this._disposed;
  }

  /** Keeps the loop drawing every frame for a while; call after changing the scene or camera. */
  public readonly requestRender = (): void => {
    this.lastActivityTime = performance.now();
  };

  /** True while pause() keeps the render loop stopped. */
  get isPaused(): boolean {
    return this.paused;
  }

  /** Stops drawing entirely, idle redraws included, until resume(). */
  pause(): void {
    if (this.paused) return;
    this.paused = true;
    this.renderer?.setAnimationLoop(null);
  }

  /** Starts drawing again with a fresh frame. A no-op once releaseGpu() has dropped the context. */
  resume(): void {
    if (this.released) return;
    if (!this.paused) return;
    this.paused = false;
    if (!this.renderer) return;
    this.startAnimationLoop();
    this.windowResize();
    this.requestRender();
  }

  /**
   * zone.js patches requestAnimationFrame, so a loop started inside Angular
   * would run change detection for the whole app on every frame.
   */
  private startAnimationLoop(): void {
    if (this.released) return;
    if (this.paused) return;
    const start = (): void => this.renderer.setAnimationLoop(() => this.animate());
    if (this.zone) {
      this.zone.runOutsideAngular(start);
    } else {
      start();
    }
  }

  /**
   * Draws one frame and returns a copy whose longer edge is at most `maxEdge`
   * pixels. The drawing buffer is not preserved, so the copy is taken right
   * after the explicit render.
   */
  captureSnapshot(maxEdge: number): Promise<Blob | null> {
    if (!this.renderer || !this.scene || !this.camera || this.contextLost) return Promise.resolve(null);
    const source = this.renderer.domElement;
    if (!source.width || !source.height) return Promise.resolve(null);
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
    const scale = Math.min(1, maxEdge / Math.max(source.width, source.height));
    const target = document.createElement('canvas');
    target.width = Math.max(1, Math.round(source.width * scale));
    target.height = Math.max(1, Math.round(source.height * scale));
    const context = target.getContext('2d');
    if (!context) return Promise.resolve(null);
    context.drawImage(source, 0, 0, target.width, target.height);
    return new Promise((resolve) => target.toBlob((blob) => resolve(blob), 'image/webp', 0.8));
  }

  /** Frees the GPU now: stops drawing, disposes the scene and drops the WebGL context. */
  releaseGpu(): void {
    if (!this.renderer) return;
    this.paused = true;
    this.released = true;
    const renderer = this.renderer;
    this.dispose();
    try {
      renderer.forceContextLoss();
    } catch {
      // A context that is already gone has nothing left to free.
    }
  }

  /**
   * Whether this frame is drawn. Input, camera movement and anything Angular
   * runs call requestRender, and tweens or the texture animation keep the loop
   * busy; otherwise an untouched scene is only redrawn now and then.
   */
  private frameMode(now: number): 'active' | 'idle' | 'skip' {
    const animating = this.animateTexture || TWEEN.getAll().length > 0;
    if (animating || now - this.lastActivityTime < RENDER_ACTIVE_WINDOW_MS) {
      if (this.renderIdle) {
        this.renderIdle = false;
        this.fpsMonitor.resume(now);
      }
      return 'active';
    }
    this.renderIdle = true;
    if (now - this.lastIdleRenderTime < IDLE_RENDER_INTERVAL_MS) return 'skip';
    this.lastIdleRenderTime = now;
    return 'idle';
  }

  get currentFPS(): number {
    return this.fpsMonitor.currentFPS;
  }

  // ── Dimensions helper ──────────────────────────────────────────────
  // Its label placement depends only on the view, so it is recomputed when
  // the camera or the canvas changes rather than on every drawn frame.
  private readonly dimensionsViewKey = new Float64Array(34);
  private dimensionsUnitSystem = '';

  /** Refreshes the dimensions helper if it is shown and the view has changed since it was placed. */
  public updateDimensionsHelper(): void {
    const helpers = this.dimensionsHelper;
    if (!helpers || !this.dimensionsHelperVisible) return;
    // The labels also carry the unit, so a unit switch re-places them too.
    const unitSystem = String(this.unitConversionService.unitSystem());
    const unitsChanged = unitSystem !== this.dimensionsUnitSystem;
    this.dimensionsUnitSystem = unitSystem;
    const viewChanged = this.captureDimensionsViewKey() || unitsChanged;
    if (!viewChanged && !helpers.some((helper) => helper.needsUpdate)) return;
    for (const helper of helpers) helper.update(this.camera);
  }

  /** Stores the current view (camera matrices and canvas size) and says whether it differs from the stored one. */
  private captureDimensionsViewKey(): boolean {
    const key = this.dimensionsViewKey;
    const world = this.camera.matrixWorld.elements;
    const projection = this.camera.projectionMatrix.elements;
    const canvas = this.renderer.domElement;
    let changed = false;
    const write = (index: number, value: number): void => {
      if (key[index] !== value) {
        key[index] = value;
        changed = true;
      }
    };
    for (let i = 0; i < 16; i++) write(i, world[i]);
    for (let i = 0; i < 16; i++) write(16 + i, projection[i]);
    write(32, canvas.width);
    write(33, canvas.height);
    return changed;
  }

  // ── Overlay priority ───────────────────────────────────────────────
  // The optimized and result meshes repeat the non-design parts at the same
  // place with a different tessellation, so where both are shown the two
  // surfaces interleave in depth (striped bosses). While such an overlay is
  // visible, input part faces are pushed back by a small fraction of the model
  // size so the overlay wins wherever the two coincide. Polygon offset cannot
  // do this: the surfaces differ by tessellation chords, not by depth rounding.
  private readonly inputDepthBias = { value: 0 };
  /** Same push for translucent parts (design space), always on: see PerFaceMaterial.translucentDepthBias. */
  private readonly translucentDepthBias = { value: 0 };
  private modelMaxDimension = 0;

  private overlayVisible(): boolean {
    const shows = (group: Group): boolean => group.visible && group.children.length > 0;
    return (
      shows(this.optimizedGroup) ||
      shows(this.optimizedGroupParametric) ||
      (this.infoGroup.visible && this.infoGroup.children.some((child) => child.visible))
    );
  }

  private updateOverlayDepthBias(): void {
    const bias = overlayDepthBiasNdc(this.modelMaxDimension, this.camera.far - this.camera.near);
    this.inputDepthBias.value = this.overlayVisible() ? bias : 0;
    this.translucentDepthBias.value = bias;
  }

  /** One query object, read every frame, so the setting can change while the editor is open. */
  private readonly reducedMotion: MediaQueryList | null =
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)')
      : null;

  /**
   * Under reduced motion every tween (camera moves, pulsing markers, drill
   * bit spins) jumps to its end state and stops, so nothing moves and the
   * render loop can settle.
   */
  private finishTweensForReducedMotion(): void {
    if (!this.reducedMotion?.matches) return;
    for (const tween of TWEEN.getAll()) {
      tween.end();
      tween.stop();
      // A finished tween left in the group restarts on the next update.
      TWEEN.remove(tween);
    }
  }

  animate(): void {
    this.animationFired = true;
    // The Render Studio has the scene staged for an offline render.
    if (this.renderSuspended) return;
    if (this.resizePending) {
      this.resizePending = false;
      this.windowResize();
    }

    // The load-case comparison covers this view with its own panels; drawing
    // the editor scene behind it would only cost frames. TWEEN still runs.
    if (this.comparisonModeActive) {
      this.finishTweensForReducedMotion();
      TWEEN.update();
      return;
    }

    const now = performance.now();
    const mode = this.frameMode(now);
    if (mode === 'skip') return;

    this.clock.update();
    const delta = this.clock.getDelta();
    if (mode === 'active') this.fpsMonitor.tick(now, delta * 1000);

    // Texture animation (throttled to 60fps for high refresh rate displays)
    if (this.animateTexture && now - this.lastTextureAnimTime >= this.textureAnimInterval) {
      this.lastTextureAnimTime = now;
      try {
        const mesh = this.session.engine?.infoGroup.children.find(
          (c) => c.userData['fea_type'] === this.session.engine?.currentFEAOutput
        ) as Mesh;
        if (mesh?.material instanceof ShaderMaterial) {
          mesh.material.uniforms['uTime'].value += 0.1;
        }
      } catch (e) {}
    }

    const tweensBefore = TWEEN.getAll().length;
    // After counting, so the change detection pass below also covers their onComplete.
    this.finishTweensForReducedMotion();
    TWEEN.update();
    // Tween callbacks run here, outside Angular: one change detection pass
    // after a tween ends shows whatever its onComplete changed.
    if (this.zone && TWEEN.getAll().length < tweensBefore) {
      this.zone.run(() => undefined);
    }

    this.viewHelper.update(delta);

    this.orbitPivot.update();

    this.selector?.updatePartLabelScales();
    this.updateAnnotationSizePreview();

    this.updateDimensionsHelper();

    this.jawsManager.updateMinGapBadgePosition();

    if (this.controls instanceof TrackballControls) {
      this.controls.update();
    }
    // Decoupled pivot persists through panning - no automatic clearing

    // Gated on clippingEnabled, not just on the clipper existing: initClipping
    // runs on every project load, and performClipping rebuilds a whole-scene
    // bounding box plus three BVH outline passes PER PART, every frame. With
    // clipping switched off that is a permanent frame-time cost for outlines
    // nobody can see.
    if (this.clippingEnabled && this.clipper) {
      this.clipper.forEach((c) => c.performClipping());
    }

    this.outlineEffect.update();
    this.updateOverlayDepthBias();

    // Nothing can be drawn until the browser restores the context.
    if (this.contextLost) return;

    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);

    this.viewHelper.render(this.renderer);
    this.renderer_css2d.render(this.scene, this.camera);
  }

  dispose(): void {
    this._disposed = true;

    // Stop animation loop
    this.renderer.setAnimationLoop(null);

    // Unsubscribe RxJS
    this.themeSubscription?.unsubscribe();
    this.zoneStableSubscription?.unsubscribe();

    this.canvas.removeEventListener('webglcontextlost', this.onContextLost);
    this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored);
    this.selector?.dispose();

    // Remove TrackballControls event listeners
    this.canvasResizeObserver?.disconnect();
    this.canvasResizeObserver = undefined;

    this.controls.removeEventListener('start', this.orbitPivot.controlsActionStartHandler);
    this.controls.removeEventListener('end', this.orbitPivot.controlsActionEndHandler);
    this.controls.removeEventListener('change', this.requestRender);
    this.controls.dispose?.();

    // Dispose manager resources
    this.keyboardShortcuts?.dispose();
    this.spaceMouse?.dispose();
    this.orbitPivot?.dispose();
    this.drillBitsManager?.disposeDrillBits();
    this.drillBitsManager?.disposeSymmetryPlanes();
    this.drillBitsManager?.showMillingDepthPlanes(null);
    this.helpers?.disposeArrow();
    this.pickedPointMarkers?.clear();

    // Dispose DragControls on all parts, and release a hold left by a drag cut short
    for (const obj of this.objects) {
      for (const key of ['part_sphere', 'part_reference_sphere']) {
        const sphere = obj.group.userData[key] as Mesh | undefined;
        const dragControl = sphere?.userData['dragControl'];
        if (dragControl?.dispose) {
          dragControl.dispose();
        }
      }
    }
    this.session?.releaseAutoSave(REMOTE_POINT_DRAG_HOLD);
    this.session?.releaseAutoSave(REFERENCE_POINT_DRAG_HOLD);

    // Dispose scene resources. Sprite extends Object3D directly, not Mesh, so
    // a Mesh-only check silently walks past every axis and part label and
    // leaks its CanvasTexture with it. Line/LineSegments are the same shape.
    this.scene.traverse((child: Object3D) => {
      const geometry = (child as Mesh).geometry;
      const material = (child as Mesh).material;

      geometry?.dispose();
      if (Array.isArray(material)) {
        (material as Material[]).forEach((m) => {
          (m as SpriteMaterial).map?.dispose();
          m.dispose();
        });
      } else if (material) {
        (material as SpriteMaterial).map?.dispose();
        (material as Material).dispose();
      }
    });

    // The cache holds the same CanvasTextures the sprites above just disposed.
    this.triadLabelTextures.clear();

    // Dispose grid
    this.disposeInfiniteGrid();

    this.wireMaterials.clear();

    // The shared PMREM environment texture is referenced by many materials'
    // envMap and by scene.environment; Material.dispose() above does not
    // dispose textures it merely references, so it needs disposing once here.
    this.envMap?.dispose();
    this.envMap = null;
    this.pmremGenerator?.dispose();
    this.pmremGenerator = undefined;

    // Dispose renderers
    this.renderer_css2d?.domElement?.remove();
    this.renderer.dispose();

    // Clear references
    this.objects.length = 0;
    this.groups.length = 0;
  }

  // -- Load case visualization delegates (implementation in LoadCaseVisualizationManager) --
  toggleReactionForces(value?: boolean): void { this.loadCaseVisualization.toggleReactionForces(value); }
  hideReactionForces(): void { this.loadCaseVisualization.hideReactionForces(); }
  refreshReactionForceVisuals(): void { this.loadCaseVisualization.refreshReactionForceVisuals(); }
  toggleLoadCaseHighlight(value?: boolean): void { this.loadCaseVisualization.toggleLoadCaseHighlight(value); }
  refreshIndicatorSizes(): void { this.loadCaseVisualization.refreshIndicatorSizes(); }
  positionGroupEditStack(group: LoadsGroupItem, force?: boolean): void { this.loadCaseVisualization.positionGroupEditStack(group, force); }

  createGroupEditStack(group: LoadsGroupItem): unknown { return this.loadCaseVisualization.createGroupEditStack(group); }
  disposeGroupEditStack(group: LoadsGroupItem): void { this.loadCaseVisualization.disposeGroupEditStack(group); }
  generateGroupEditStack(group: LoadsGroupItem): void { this.loadCaseVisualization.generateGroupEditStack(group); }
  setForceAndMomentWithManipulator(updateControl: boolean = false, group: LoadsGroupItem): void { this.loadCaseVisualization.setForceAndMomentWithManipulator(updateControl, group); }

  // Helpers
  /** The device pixel ratio the canvas renders at, capped at MAX_PIXEL_RATIO. */
  get pixelRatio(): number {
    return Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
  }

  get canvasWidth() {
    return (this.canvas.clientWidth * this.pixelRatio) | 0;
  }

  get canvasHeight() {
    return (this.canvas.clientHeight * this.pixelRatio) | 0;
  }

  get canvasAspect() {
    return this.canvasWidth / this.canvasHeight;
  }



  private static emptyBoundingBox(): { center: Vector3; size: Vector3; box: Box3 } {
    const d = DEFAULT_EMPTY_SIZE;
    return {
      center: new Vector3(0, 0, 0),
      size: new Vector3(d, d, d),
      box: new Box3(new Vector3(-d / 2, -d / 2, -d / 2), new Vector3(d / 2, d / 2, d / 2)),
    };
  }

  private static boxToInfo(box: Box3): { center: Vector3; size: Vector3; box: Box3 } {
    if (box.isEmpty()) return Engine.emptyBoundingBox();
    const center = new Vector3();
    const size = new Vector3();
    box.getCenter(center);
    box.getSize(size);
    return { center, size, box };
  }

  /** Exact bounds of the group's part meshes in world space, or under `extra` applied after it. */
  public calculateGroupMinMaxCenter(group: Group, extra?: Matrix4): { center: Vector3; size: Vector3; box: Box3 } {
    const min = new Vector3(Infinity, Infinity, Infinity);
    const max = new Vector3(-Infinity, -Infinity, -Infinity);
    const vertex = new Vector3();
    let hasVertices = false;

    group.traverse((child) => {
      if (
        child instanceof Mesh &&
        child.isMesh &&
        child.geometry instanceof BufferGeometry &&
        ('isMergedMesh' in child.userData || 'faceId' in child.userData)
      ) {
        const position = child.geometry.attributes.position;
        if (!position || !(position instanceof BufferAttribute)) return;

        for (let i = 0; i < position.count; i++) {
          hasVertices = true;
          vertex.fromBufferAttribute(position, i);
          vertex.applyMatrix4(child.matrixWorld);
          if (extra) vertex.applyMatrix4(extra);
          min.min(vertex);
          max.max(vertex);
        }
      }
    });

    if (!hasVertices) return Engine.emptyBoundingBox();

    const box = new Box3(min, max);
    const center = new Vector3().addVectors(min, max).multiplyScalar(0.5);
    const size = new Vector3().subVectors(max, min);
    return { center, size, box };
  }

  private _cachedObjectInfo!: { center: Vector3; size: Vector3; box: Box3 };
  private _cachedObjectInfoUpdateNeeded = true;
  public invalidateCachedObjectInfo(): void {
    this._cachedObjectInfoUpdateNeeded = true;
    this._cachedUcsFrameInfo = null;
    this._cachedDesignUcsFrameInfo = null;
    this._cachedDesignRegionUcsFrameInfo = null;
    // The jaws preview caches the same geometry in pivot space; it has to be
    // rebuilt whenever the part list changes or it will measure stale parts.
    this.jawsManager?.invalidateVertexCache();
  }

  /**
   * The exact bounds of the part meshes, whatever the UCS. Frame markers and
   * part labels also live in the model group, so a plain Box3 of the group
   * would move with them.
   */
  get objectInfo(): { center: Vector3; size: Vector3; box: Box3 } {
    if (this._cachedObjectInfoUpdateNeeded || !this._cachedObjectInfo) {
      this.pivot.updateWorldMatrix(true, true);
      this.group.updateWorldMatrix(true, true);
      this._cachedObjectInfoUpdateNeeded = false;
      this._cachedObjectInfo = this.calculateGroupMinMaxCenter(this.group);
    }
    return this._cachedObjectInfo;
  }

  private _cachedUcsFrameInfo: { center: Vector3; size: Vector3; box: Box3 } | null = null;

  /**
   * The model's bounds in UCS-frame coordinates, for helpers placed in
   * `ucsFrame`: measured as the run turns the model. The same as `objectInfo`
   * with the UCS off.
   */
  get ucsFrameObjectInfo(): { center: Vector3; size: Vector3; box: Box3 } {
    const ucs = this.session.project.userCoordinateSystem;
    if (!ucs?.enabled || !this.ucsViewReady) return this.objectInfo;
    if (!this._cachedUcsFrameInfo) {
      this.pivot.updateWorldMatrix(true, true);
      this._cachedUcsFrameInfo = this.calculateGroupMinMaxCenter(this.group, this.ucsTurn());
    }
    return this._cachedUcsFrameInfo;
  }

  private _cachedDesignUcsFrameInfo: { key: string; info: { center: Vector3; size: Vector3; box: Box3 } | null } | null = null;

  /**
   * The design parts' bounds in UCS-frame coordinates, the frame the optimizer
   * measures them in. Null with no design part. Measured like `ucsFrameObjectInfo`.
   */
  get designUcsFrameInfo(): { center: Vector3; size: Vector3; box: Box3 } | null {
    const design = this.objectsDesign;
    const key = design.map((o) => o.group.uuid).join(',');
    if (this._cachedDesignUcsFrameInfo?.key === key) return this._cachedDesignUcsFrameInfo.info;

    const ucs = this.session.project.userCoordinateSystem;
    this.pivot.updateWorldMatrix(true, true);
    this.group.updateWorldMatrix(true, true);
    const pivotTurn = ucs?.enabled && this.ucsViewReady ? this.ucsTurn() : undefined;

    const box = new Box3();
    for (const object of design) {
      // A part with no mesh measures as the default empty box, which is not part of the model.
      if (!this.hasPartMesh(object.group)) continue;
      box.union(this.calculateGroupMinMaxCenter(object.group, pivotTurn).box);
    }
    const info = box.isEmpty() ? null : Engine.boxToInfo(box);
    this._cachedDesignUcsFrameInfo = { key, info };
    return info;
  }

  private _cachedDesignRegionUcsFrameInfo: { key: string; info: { center: Vector3; size: Vector3; box: Box3 } | null } | null = null;

  /**
   * The design region's bounds in UCS-frame coordinates: the design parts
   * with every non-design part taken away, the region the runner places
   * milling limits from. Estimated from the meshes (see
   * `estimateDesignRegion`); the design parts' own bounds when there is no
   * non-design part in the way or the estimate cannot be made. Null with no
   * design part, or when the non-design parts cover the design entirely.
   */
  get designRegionUcsFrameInfo(): { center: Vector3; size: Vector3; box: Box3 } | null {
    const designInfo = this.designUcsFrameInfo;
    const nondesign = this.objectsNondesign;
    if (!designInfo || nondesign.length === 0) return designInfo;

    const key = [...this.objectsDesign, ...nondesign].map((o) => o.group.uuid).join(',');
    if (this._cachedDesignRegionUcsFrameInfo?.key === key) return this._cachedDesignRegionUcsFrameInfo.info;

    const ucs = this.session.project.userCoordinateSystem;
    this.pivot.updateWorldMatrix(true, true);
    this.group.updateWorldMatrix(true, true);
    const pivotTurn = ucs?.enabled && this.ucsViewReady ? this.ucsTurn() : undefined;
    const soups = (objects: EngineObject[]): TriangleSoup[] =>
      objects.map((o) => this.partTriangles(o.group, pivotTurn)).filter((soup): soup is TriangleSoup => soup !== null);

    const estimate = estimateDesignRegion(soups(this.objectsDesign), soups(nondesign));
    const info =
      estimate.kind === 'box' ? Engine.boxToInfo(estimate.box) : estimate.kind === 'covered' ? null : designInfo;
    this._cachedDesignRegionUcsFrameInfo = { key, info };
    return info;
  }

  /** The part's triangles in world space; null when it has no mesh. */
  partTriangleSoup(object: EngineObject): TriangleSoup | null {
    this.group.updateWorldMatrix(true, true);
    return this.partTriangles(object.group);
  }

  /**
   * The part's triangles in world space (or under `extra` applied after it),
   * from its merged mesh, or from its face meshes when it has none -- never
   * both, which would count every surface twice.
   */
  private partTriangles(group: Group, extra?: Matrix4): TriangleSoup | null {
    const merged: Mesh[] = [];
    const faces: Mesh[] = [];
    group.traverse((child) => {
      if (!(child instanceof Mesh) || !(child.geometry instanceof BufferGeometry)) return;
      if ('isMergedMesh' in child.userData) merged.push(child);
      else if ('faceId' in child.userData) faces.push(child);
    });
    const meshes = merged.length > 0 ? merged : faces;

    const out: number[] = [];
    const vertex = new Vector3();
    for (const mesh of meshes) {
      const position = mesh.geometry.attributes['position'];
      if (!position || !(position instanceof BufferAttribute)) continue;
      const index = mesh.geometry.index;
      const count = index ? index.count : position.count;
      for (let i = 0; i < count; i++) {
        vertex.fromBufferAttribute(position, index ? index.getX(i) : i);
        vertex.applyMatrix4(mesh.matrixWorld);
        if (extra) vertex.applyMatrix4(extra);
        out.push(vertex.x, vertex.y, vertex.z);
      }
    }
    return out.length >= 9 ? new Float32Array(out) : null;
  }

  private hasPartMesh(group: Group): boolean {
    let found = false;
    group.traverse((child) => {
      if (!found && child instanceof Mesh && ('isMergedMesh' in child.userData || 'faceId' in child.userData)) found = true;
    });
    return found;
  }

  getObjectBbox(object: Object3D): { center: Vector3; size: Vector3; box: Box3 } {
    return Engine.boxToInfo(new Box3().setFromObject(object));
  }

  applyThemeToGrid(): void { this.gridManager.applyTheme(); }

  /////////////////////////////// SOFTJAWS ///////////////////////////////
  public jaws: {
    fixed: {
      mesh?: Mesh;
      edges?: LineSegments;
    };
    movable: {
      mesh?: Mesh;
      edges?: LineSegments;
    };
    group: Group;
    result: Group;
    jawsPivot: Group;
    resources: Object3D[];
  } = {
    fixed: {
      mesh: undefined,
      edges: undefined
    },
    movable: {
      mesh: undefined,
      edges: undefined
    },
    group: new Group(),
    result: new Group(),
    jawsPivot: new Group(),
    resources: []
  };

  // ── Backward-compatible delegates to JawsManager ─────────────────────

  initJaws(): void { this.jawsManager.initJaws(); }
  updateJaws(): {
    effectiveJawWall: number;
    effectiveJawGap: number;
    atMinGap: boolean;
  } | undefined { return this.jawsManager.updateJaws(); }
  async addJawsResult(resultProtoUrl: string, onProgress?: (progress: JawsResultProgress) => void): Promise<void> { return this.jawsManager.addJawsResult(resultProtoUrl, onProgress); }
  removeJawsResult(): void { this.jawsManager.removeJawsResult(); }

  /**
   * Add a generated shape part to the engine
   * @param protoData - ArrayBuffer containing the proto data
   * @param partId - The ID of the part to add
   * @param partName - The name of the part
   */
  async addGeneratedShapePart(protoData: ArrayBuffer, partId: string, partName: string): Promise<void> {
    const decoded = await this.decodeAssemblyProto(protoData);

    // Find the specific part in the assembly
    const partData = decoded.parts?.find((p: ProtoPart) => p.id === partId);
    if (!partData) {
      console.warn(`Part ${partId} not found in assembly proto`);
      return;
    }

    const group = new Group();
    group.name = partName;
    group.userData = { id: partId, pid: partData.parentId, source: 'shape' };

    // Build the part as a single merged mesh with PerFaceMaterial — same structure
    // as initMesh, so design/non-design coloring, face picking, and every other
    // merged-mesh-aware path works identically for imported parts.
    const cutOffAngle = GENERATED_SHAPE_CUTOFF_ANGLE;

    const processedFaces = await Promise.all(
      (partData.faces || []).map(async (face: ProtoFace) => {
        const geometry = await this.processFaceGeometry(
          new Float32Array(face.vertices),
          new Uint32Array(face.indices),
          cutOffAngle
        );
        return { geometry, faceId: face.id };
      })
    );

    const faceGeometries: BufferGeometry[] = [];
    const faces: string[] = [];
    for (const { geometry, faceId } of processedFaces) {
      faceGeometries.push(geometry);
      faces.push(faceId);
    }

    const partColor = readablePartColor(partData.color || '#6366f1');
    const { geometry: mergedGeometry, faceIdToIndex } = mergeGeometriesWithFaceIndex(faceGeometries, faces);

    const perFaceMaterial = new PerFaceMaterial(
      {
        color: partColor,
        side: DoubleSide,
        vertexColors: false,
        flatShading: false,
        opacity: 1,
        transparent: false,
        metalness: PART_METALNESS,
        reflectivity: PART_REFLECTIVITY,
        roughness: PART_ROUGHNESS,
        clearcoat: PART_CLEARCOAT,
        envMap: this.envMap,
        envMapIntensity: PART_ENVMAP_INTENSITY,
        clippingPlanes: this.clippingPlanes,
        ...FACE_POLYGON_OFFSET
      },
      faces,
      partColor
    );
    perFaceMaterial.depthBias = this.inputDepthBias;
    perFaceMaterial.translucentDepthBias = this.translucentDepthBias;

    const meshobj = new Mesh(mergedGeometry, perFaceMaterial);
    this.computeBVHAsync(meshobj.geometry).catch(() => {});
    meshobj.name = meshobj.uuid;
    meshobj.userData = {
      id: partId,
      parentId: partData.parentId,
      originalColor: partColor,
      faceIds: faces,
      faceIdToIndex: faceIdToIndex,
      perFaceMaterial: perFaceMaterial,
      isMergedMesh: true
    };
    group.add(meshobj);

    const wirematerial = this.createWireLineMaterial();

    const { wiresGroup, wireNames: wires } = this.createMergedWireGroup(partData.wires || [], wirematerial, 'AssemblyMeshLine');
    group.add(wiresGroup);

    this.createPartHelpers(group);

    // Add to scene
    this.group.add(group);
    this.groups.push(group);

    // Add to objects list
    this.objects.push({
      group: group,
      data: partData,
      faces: faces,
      wires: wires,
      wirematerial: wirematerial
    });

    // Sort objects by name
    this.objects.sort((a, b) => {
      if (a.data.name < b.data.name) return -1;
      if (a.data.name > b.data.name) return 1;
      return 0;
    });

    // Update world matrices so COM/center calculations are correct
    this.pivot.updateMatrixWorld(true);

    // Recalculate bounds and update scene
    this.invalidateCachedObjectInfo();
    this.loadCaseVisualization.refreshIndicatorSizes();
    this.controls.target.copy(this.toViewSpace(this.objectInfo.center));
    this.rebuildViewHelpers();
    this.windowResize();
  }

  /**
   * Removes a part from the scene by its ID
   * @param partId - The ID of the part to remove
   * @returns true if part was found and removed, false otherwise
   */
  removePartById(partId: string): boolean {
    const objectIndex = this.objects.findIndex((o) => o.data.id === partId);
    if (objectIndex === -1) {
      console.warn(`Part ${partId} not found in engine objects`);
      return false;
    }

    const obj = this.objects[objectIndex];
    
    // Remove from outline selection if present
    obj.group.children.forEach((child: Object3D) => {
      if (child instanceof Mesh) {
        this.outlineEffect.selection.delete(child);
      }
    });

    // Dispose of geometries and materials
    obj.group.traverse((child: Object3D) => {
      if ((child as Mesh).geometry) {
        (child as Mesh).geometry.dispose();
      }
      if ((child as Mesh).material) {
        if (Array.isArray((child as Mesh).material)) {
          ((child as Mesh).material as Material[]).forEach((m: Material) => m.dispose());
        } else {
          ((child as Mesh).material as Material).dispose();
        }
      }
    });

    // Remove from scene group
    this.group.remove(obj.group);
    
    // Remove from groups array
    const groupIndex = this.groups.indexOf(obj.group);
    if (groupIndex !== -1) {
      this.groups.splice(groupIndex, 1);
    }

    // Remove from objects array
    this.objects.splice(objectIndex, 1);

    // Update world matrices and recalculate bounds
    this.pivot.updateMatrixWorld(true);
    this.invalidateCachedObjectInfo();
    this.controls.target.copy(this.toViewSpace(this.objectInfo.center));
    this.rebuildViewHelpers();
    this.windowResize();

    return true;
  }

  /**
   * Removes multiple parts from the scene by their IDs
   * @param partIds - Array of part IDs to remove
   * @returns Array of IDs that were successfully removed
   */
  removePartsById(partIds: string[]): string[] {
    const removedIds: string[] = [];
    for (const partId of partIds) {
      if (this.removePartById(partId)) {
        removedIds.push(partId);
      }
    }
    return removedIds;
  }

  /**
   * Reloads and syncs parts from an updated assembly proto
   * Adds new parts that don't exist and keeps existing ones
   * @param protoData - The new proto data as ArrayBuffer
   * @returns Object containing arrays of added part IDs/names
   */
  async syncPartsFromProto(protoData: ArrayBuffer): Promise<{ addedParts: { id: string; name: string }[] }> {
    const decoded = await this.decodeAssemblyProto(protoData);
    const existingIds = new Set(this.objects.map(o => o.data.id));
    const addedParts: { id: string; name: string }[] = [];
    
    // Add parts that don't exist yet
    for (const partData of decoded.parts || []) {
      if (!existingIds.has(partData.id)) {
        await this.addGeneratedShapePart(protoData, partData.id, partData.name);
        addedParts.push({ id: partData.id, name: partData.name });
      }
    }

    if (addedParts.length > 0) {
      this.session.updateObjectTypes();
    }

    return { addedParts };
  }

  checkJawCollisions(): { fixedJawIntersects: boolean; movableJawIntersects: boolean } { return this.jawsManager.checkJawCollisions(); }
  updateJawColors(fixedIntersects: boolean, movableIntersects: boolean): void { this.jawsManager.updateJawColors(fixedIntersects, movableIntersects); }
  rotateModelToFace(faceNormal: Vector3, onComplete: (modelRotation: { x: number; y: number; z: number }) => void): void { this.jawsManager.rotateModelToFace(faceNormal, onComplete); }
  enableRotationGizmo(onChange: (rotation: { x: number; y: number; z: number }) => void): void { this.jawsManager.enableRotationGizmo(onChange); }
  disableRotationGizmo(): void { this.jawsManager.disableRotationGizmo(); }
  updateRotationGizmo(): void { this.jawsManager.updateRotationGizmo(); }

  // ═══════════════════════════════════════════════════════════════════════════
  // CAD EDITOR VISIBILITY MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════

  // ── Backward-compatible delegates to CADEditorVisibilityManager ─────
  hideAllObjectsForCADEditor(): void { this.cadEditorVisibility.hideAll(); }
  restoreAllObjectsForCADEditor(): void { this.cadEditorVisibility.restoreAll(); }
}
export { Engine };
